import { apiFetch, usarSesionDeSoporte } from './api.js';
import { Toast } from '../comun/toast.js';
import { iniciarReporteDeErrores } from '../comun/errores.js';
import { montarBotonTema, PANEL_ABIERTO } from '../comun/tema.js';
import { campoTelefono, MSG_TELEFONO, telefonoLegible } from '../comun/telefono.js';
import { iconoEvento, textoEvento } from '../comun/eventos.js';
import { esc } from '../comun/html.js';
import { plural } from '../comun/texto.js';
import { registrarSW } from './sw-registro.js';
import { fijarAltoCabecera } from '../comun/cabecera.js';

/**
 * El teléfono personal del perfil: país + número (24 ago 2026).
 *
 * Se monta una sola vez y se conserva, porque el modal de perfil se abre y se cierra sin
 * destruirse. El país no se elige acá: sale del número ya guardado, que está en E.164.
 */
let telPerfil = null;
function perfilTelefono() {
  if (telPerfil) return telPerfil;
  const caja = document.getElementById('prof-telefono-caja');
  if (!caja) return null;
  telPerfil = campoTelefono({ placeholder: '9 1234 5678' });
  caja.appendChild(telPerfil.nodo);
  return telPerfil;
}

// Primero de todo: si algo de aca abajo revienta al cargar, se quiere que quede reportado.
iniciarReporteDeErrores('portal');

// El alto real de la barra de arriba, en `--cab`, para que la lateral y todo lo que se cuelgue
// debajo se posicionen contra una medida y no contra un numero escrito a mano. El portal no monta
// `montarCabecera` --su barra esta en `index.html` con la campana, los mensajes y el perfil-- pero
// necesita exactamente la misma medida. Ver `comun/cabecera.js`.
fijarAltoCabecera(document.querySelector('nav.nav'));

const API = {
  login: 'https://api.deviant.cl/portal/login',
  estado: 'https://api.deviant.cl/portal/estado',
  // Fuera del corte por impago a propósito: es la ruta que lo explica. Ver portal-cuenta.js.
  servicio: 'https://api.deviant.cl/portal/servicio',
  canales: 'https://api.deviant.cl/portal/canales',
  botToggle: 'https://api.deviant.cl/portal/bot-toggle',
  // El interruptor del catalogo (0202): se guarda por la ruta del catalogo, no por la del agente.
  catalogoAjustes: 'https://api.deviant.cl/portal/catalogo/ajustes',
  // Los planes (0236): el interruptor se guarda por su ruta, igual que el del catálogo.
  planes: 'https://api.deviant.cl/portal/planes',
  planesAjustes: 'https://api.deviant.cl/portal/planes/ajustes',
  // Primer endpoint movido a la API propia (tarea 1.8, 31 jul 2026). El resto sigue
  // en n8n hasta que este se verifique en el navegador: el corte es de a uno.
  notificaciones: 'https://api.deviant.cl/portal/notificaciones',
  logout: 'https://api.deviant.cl/portal/logout',
  derivaciones: 'https://api.deviant.cl/portal/derivaciones',
  recoveryRequest: 'https://api.deviant.cl/portal/recuperar-solicitud',
  recoveryReset: 'https://api.deviant.cl/portal/recuperar-reset',
  perfilActualizar: 'https://api.deviant.cl/portal/perfil-actualizar',
  // FIX: se reutiliza el mismo webhook de recuperación de contraseña,
  // ya que genera y envía el código de la misma forma. No existe (ni hace falta)
  // un endpoint separado 'perfil-codigo-correo'.
  perfilCodigoCorreo: 'https://api.deviant.cl/portal/recuperar-solicitud',
  perfilValidarCorreo: 'https://api.deviant.cl/portal/perfil-validar-correo',
  chatStatus: 'https://api.deviant.cl/portal/chat-status',
  agente: 'https://api.deviant.cl/portal/agente',
  agenteVersiones: 'https://api.deviant.cl/portal/agente/versiones',
  agenteRevertir: 'https://api.deviant.cl/portal/agente/revertir',
  agentePrevisualizar: 'https://api.deviant.cl/portal/agente/previsualizar',
  // Los documentos de la base de conocimiento, de solo lectura: los sube Deviant (0215).
  agenteConocimiento: 'https://api.deviant.cl/portal/agente/conocimiento',
  reconectar: 'https://api.deviant.cl/portal/instancia-reconectar',
  // Cerrar la sesión de WhatsApp para entrar con OTRO número. `reconectar` levanta la misma
  // sesión con las credenciales guardadas, así que nunca pide un QR nuevo.
  desvincular: 'https://api.deviant.cl/portal/instancia-desvincular',
  // El código de 8 caracteres para vincular sin escanear. Lo emite WhatsApp y sólo contra un
  // número, así que esta ruta pide el número y `desvincular` no.
  codigoVinculacion: 'https://api.deviant.cl/portal/instancia-codigo',
  metricas: 'https://api.deviant.cl/portal/metricas',
  actividad: 'https://api.deviant.cl/portal/actividad',
  // Ventana de respuesta de 24 h (3.9). Detecta y avisa; no bloquea nada.
  ventana: 'https://api.deviant.cl/portal/ventana',
  sesion: 'https://api.deviant.cl/portal/sesion',
  pagos: 'https://api.deviant.cl/portal/pagos',
  pagoTarjeta: 'https://api.deviant.cl/portal/pago/mercadopago',
  // Accesos (29 ago 2026). `usuarios` ya existía para la pantalla del calendario; acá se reusa.
  usuarios: 'https://api.deviant.cl/portal/usuarios',
  usuarioAccesos: 'https://api.deviant.cl/portal/usuario/accesos'
};

let token=null, usuario=null, cliente=null, suscripcion=null, refreshInterval=null;

// RBAC (2.14). Esconder el control es prolijidad, no seguridad: quien decide es la API,
// que devuelve 403. Acá solo evitamos ofrecerle al agente un botón que le va a fallar.
const OPERA_CANAL = () => ['dueno', 'administrador'].includes((usuario && usuario.rol) || '');

// Un usuario cuyo rol es `agenda` no entra al panel: la API le responde 403 a todo lo de acá. Se lo
// manda a su agenda apenas se sabe el rol, para que no vea el tablero dibujarse y vaciarse. La
// seguridad ya está del lado del servidor; esto es no mostrarle una pantalla que va a fallar.
const SOLO_AGENDA = () => ((usuario && usuario.rol) || '') === 'agenda';
function irAAgendaSiCorresponde() {
  if (!SOLO_AGENDA()) return false;
  location.replace('./agenda.html');
  return true;
}

// Las pantallas del calendario rebotan acá con `?volver=` cuando la sesión no vale (`alAcceso` en
// agenda-ui.js). Sin esto, quien entra por `calendario.deviant.cl` —un nombre que se teclea a
// propósito para ver el calendario— termina el login en el portal y tiene que ir a buscar dónde
// iba. Le pasaba igual a cualquier enlace guardado del calendario.
//
// ⚠️ **Lista blanca, y la URL se reconstruye acá.** Obedecer el valor tal cual sería un open
// redirect en la pantalla de acceso: bastaría un link con `?volver=https://otro-sitio` para mandar
// a cualquier parte justo después de que alguien tecleó su contraseña. Se acepta el nombre de la
// página y nada más; la query se conserva porque `agenda.html?dia=` y `agenda-config.html?p=avisos`
// la leen, y va a una página nuestra, que es lo que la vuelve inofensiva.
const PAGINAS_DE_VUELTA = new Set(['agenda.html', 'agenda-config.html']);

// ⚠️ **Un solo reintento, o esto es un ciclo infinito.** La vuelta es un rebote entre dos páginas:
// el calendario manda acá con `?volver=` cuando recibe un 401, y acá se lo devuelve. Si la página
// de destino vuelve a rechazar --porque el perfil de agenda no alcanza, porque la ruta contesta 403,
// porque lo que sea-- el par se queda rebotando y el navegador no muestra ni una pantalla: eso es el
// «ciclo» que se reportó el 21 oct 2026. Con la marca, el segundo intento seguido se queda en el
// portal, que es una pantalla desde la cual se puede hacer algo.
//
// La ventana de 10 s es lo que separa un rebote de una visita legítima: un ciclo da la vuelta en
// milisegundos y nadie abre el calendario dos veces en diez segundos. Caduca sola, así que no hay
// nada que limpiar y una marca olvidada no deja a nadie sin su vuelta.
const MARCA_VUELTA = 'dv_vuelta';
function reboteSeguido(pagina) {
  try {
    const previo = JSON.parse(sessionStorage.getItem(MARCA_VUELTA) || 'null');
    sessionStorage.setItem(MARCA_VUELTA, JSON.stringify({ p: pagina, t: Date.now() }));
    return !!previo && previo.p === pagina && Date.now() - previo.t < 10000;
  } catch {
    // Sin `sessionStorage` (ventana privada, almacenamiento bloqueado) no hay cómo detectarlo. Se
    // deja pasar: el rebote es raro y quedarse siempre en el portal rompería el caso normal.
    return false;
  }
}

function volverADondeIba() {
  const pedida = new URLSearchParams(location.search).get('volver');
  if (!pedida) return false;
  const corte = pedida.indexOf('?');
  const pagina = corte === -1 ? pedida : pedida.slice(0, corte);
  if (!PAGINAS_DE_VUELTA.has(pagina)) return false;
  if (reboteSeguido(pagina)) {
    // Se saca el `?volver=` de la barra para que recargar no vuelva a intentarlo, y se dice por qué
    // quedó acá: sin una palabra, aterrizar en el panel cuando se pidió el calendario se lee como
    // que el enlace está roto.
    history.replaceState(null, '', location.pathname);
    Toast.error('No pudimos abrir el calendario con esta cuenta. Te dejamos en el panel.');
    return false;
  }
  location.replace(`./${pagina}${corte === -1 ? '' : pedida.slice(corte)}`);
  return true;
}

// Las dos razones para no quedarse en el portal, en un solo lugar: el destino que el usuario pedía
// y el rol que no tiene panel. El explícito gana — es lo que la persona estaba intentando abrir, y
// la API igual decide si puede.
function salirDelPortalSiCorresponde() {
  return volverADondeIba() || irAAgendaSiCorresponde();
}
// Facturación es solo del dueño. Mismo criterio: la API responde 403, esto solo evita
// pedir algo que va a fallar y mostrar una sección vacía sin explicación.
const VE_FACTURACION = () => ((usuario && usuario.rol) || '') === 'dueno';
let botLocks = {};

// === EXPOSICIÓN GLOBAL PARA EL HTML ===
// Como esto es un módulo, debemos conectar las funciones al objeto 'window' 
// para que los botones con onclick="" en el HTML funcionen.
window.openModal = function(e) {
  e.preventDefault();
  document.getElementById('recovery-modal').classList.add('active');
};

// El código del correo de bienvenida (o uno pedido antes) se escribe acá sin pedir otro: pedirlo de
// nuevo invalida el que la persona tiene en la mano. Deja el correo a la vista porque el paso 2 lo usa.
window.irAlCodigo = function(e, email) {
  if (e) e.preventDefault();
  document.getElementById('recovery-modal').classList.add('active');
  if (email) document.getElementById('recovery-email').value = email;
  document.getElementById('btn-request-code').style.display = 'none';
  document.getElementById('recovery-ya-tengo').style.display = 'none';
  document.getElementById('recovery-step-2').style.display = 'block';
  document.getElementById('recovery-subtitle').textContent = 'Escribe el código que te llegó por correo y elige tu contraseña.';
  document.getElementById(email ? 'recovery-code' : 'recovery-email').focus();
};

// El botón de `cuenta-nueva` llega con `#definir=<correo>` (ver `enlaceDefinirClave` en la API).
(function abrirDesdeElCorreo() {
  const m = (location.hash || '').match(/^#definir=(.+)$/);
  if (!m) return;
  history.replaceState(null, '', location.pathname + location.search);
  let email = '';
  try { email = decodeURIComponent(m[1]); } catch { /* enlace roto: se abre igual, sin correo */ }
  irAlCodigo(null, email);
})();

window.closeModal = function() {
  document.getElementById('recovery-modal').classList.remove('active');
  // Reseteamos el formulario visualmente al cerrar por si se equivocaron
  document.getElementById('btn-request-code').style.display = '';
  document.getElementById('recovery-ya-tengo').style.display = 'block';
  document.getElementById('recovery-subtitle').textContent = 'Te enviamos un código de 6 dígitos.';
  document.getElementById('recovery-step-1').style.display = 'block';
  document.getElementById('recovery-step-2').style.display = 'none';
  document.getElementById('recovery-error').style.display = 'none';
};

// === UX: CERRAR MODAL CON CLIC AFUERA O TECLA ESC ===
const recoveryModal = document.getElementById('recovery-modal');
recoveryModal.addEventListener('click', (e) => {
  // Si el clic fue directamente en el overlay oscuro y no adentro de la caja
  if (e.target === recoveryModal) {
    closeModal();
  }
});

document.addEventListener('keydown', (e) => {
  // Si presiona Escape y el modal está abierto
  if (e.key === 'Escape' && recoveryModal.classList.contains('active')) {
    closeModal();
  }
});

// === UX: SOPORTE PARA TECLA ENTER EN MODAL ===
document.getElementById('recovery-email').addEventListener('keydown', (e) => {
  if (e.key !== 'Enter') return;
  // Con «Ya tengo un código» el botón de pedir está oculto, y pedir uno invalidaría el que tiene.
  const pedir = document.getElementById('btn-request-code');
  if (pedir.style.display === 'none') document.getElementById('recovery-code').focus();
  else pedir.click();
});

['recovery-code', 'recovery-new-password'].forEach(id => {
  document.getElementById(id).addEventListener('keydown', (e) => {
    if (e.key === 'Enter') document.getElementById('btn-reset-password').click();
  });
});



window.showToast = function(msg, type='') {
  if (type === 'error') Toast.error(msg);
  else if (type === 'success') Toast.success(msg);
  else Toast.info(msg);
};
window.toggleBot = toggleBot;
window.fetchEstado = fetchEstado;
window.performLogout = performLogout; // <-- EXPUESTA GLOBALMENTE
window.showScreen = showScreen;

function showScreen(id){document.querySelectorAll('.screen').forEach(s=>s.classList.remove('active'));document.getElementById(id).classList.add('active');}

// En móvil la bandeja es una hoja casi de pantalla completa (ver styles.css), así que hay que
// frenar el scroll de lo que queda detrás: sin esto, el dedo arrastra la página de atrás y la hoja
// se queda quieta encima, que se siente como que la app se colgó. Misma clase que usa el cajón.
const MOVIL = () => window.innerWidth <= 700; // el mismo corte que el @media de styles.css
function cerrarNoti() {
  document.getElementById('noti-dropdown-panel').classList.remove('open');
  document.body.classList.remove('sin-scroll');
}
document.getElementById('btn-noti-toggle').addEventListener('click', (e) => {
  e.stopPropagation();
  const panel = document.getElementById('noti-dropdown-panel');
  const abierto = panel.classList.toggle('open');
  if (MOVIL()) document.body.classList.toggle('sin-scroll', abierto);
  // Al abrir, avisar para que se cierre lo que hubiera abierto —hoy el panel de tema—. Ver
  // PANEL_ABIERTO en comun/tema.js: el `stopPropagation()` de dos líneas más arriba es
  // obligatorio (sin él este mismo clic cerraría la bandeja al instante) y es también lo que
  // impide que el clic llegue a `document`, que es de donde el otro panel esperaba su señal.
  if (abierto) document.dispatchEvent(new CustomEvent(PANEL_ABIERTO, { detail: { origen: panel } }));
});
document.addEventListener('click', cerrarNoti);
document.addEventListener(PANEL_ABIERTO, (e) => {
  if (e.detail?.origen !== document.getElementById('noti-dropdown-panel')) cerrarNoti();
});
document.getElementById('btn-cerrar-noti').addEventListener('click', (e) => {
  e.stopPropagation();
  cerrarNoti();
});
document.getElementById('noti-dropdown-panel').addEventListener('click', (e) => e.stopPropagation());

function saveSession(tok, usr, cli, sub){
  // El token ya NO se guarda: vive en una cookie httpOnly que el JavaScript no puede
  // leer (2.13). Lo que queda en sessionStorage son datos de pantalla, no credenciales:
  // un XSS que los lea no se lleva la sesión. 'dv_tok' pasa a ser una marca de que hay
  // sesión abierta, para que el resto del código siga funcionando igual.
  sessionStorage.setItem('dv_tok','cookie');
  sessionStorage.setItem('dv_usr',JSON.stringify(usr));
  sessionStorage.setItem('dv_cli',JSON.stringify(cli));
  sessionStorage.setItem('dv_sub',JSON.stringify(sub));
}

/**
 * Sesion de soporte abierta desde el panel admin (4.12).
 *
 * El token llega en el FRAGMENTO de la URL (#soporte=...) y no como query string: el
 * fragmento no viaja al servidor, asi que no queda en los logs de acceso de nginx ni en
 * el Referer. Se consume y se borra de la barra de direcciones en el acto.
 *
 * Esta sesion dura 30 minutos y no tiene refresco: cuando expire, el admin vuelve a
 * pedirla desde el panel y eso queda registrado otra vez. Es a proposito -- que renovar
 * cueste un registro nuevo es lo que hace util la bitacora.
 */
function tokenDeSoporte(){
  const m = (location.hash || '').match(/soporte=([a-f0-9]{64})/i);
  if(!m) return null;
  // history.replaceState y no location.hash='': lo segundo deja el '#' y recarga.
  history.replaceState(null, '', location.pathname + location.search);
  return m[1];
}

function loadSession(){
  const soporte = tokenDeSoporte();
  if(soporte){
    // No se guarda en sessionStorage: una sesion de soporte no tiene que sobrevivir a
    // que el admin recargue la pestaña sin querer.
    token = soporte;
    usarSesionDeSoporte(soporte);
    return 'soporte';
  }
  const tok=sessionStorage.getItem('dv_tok'), usr=sessionStorage.getItem('dv_usr'), cli=sessionStorage.getItem('dv_cli'), sub=sessionStorage.getItem('dv_sub');
  if(tok && usr && cli){ token = tok; usuario = JSON.parse(usr); cliente = JSON.parse(cli); suscripcion = sub ? JSON.parse(sub) : null; return true; }
  return false;
}

function clearSession(){
  sessionStorage.clear();
  token=null; usuario=null; cliente=null; suscripcion=null;
}

document.getElementById('btn-login').addEventListener('click',async()=>{
  const email=document.getElementById('login-email').value.trim(), password=document.getElementById('login-password').value;
  const btn=document.getElementById('btn-login'),err=document.getElementById('login-error');
  err.style.display='none';
  if(!email||!password){err.textContent='Por favor completa todos los campos.';err.style.display='block';return;}
  btn.disabled=true; btn.innerHTML='<span class="spinner"></span> Ingresando...';
  
  try{
    // Uso del nuevo apiFetch para protección de Timeout
    const data = await apiFetch(API.login, {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify({email, password})
    });
    
    if(data.success){
      // data.token ya no viene: la sesión la sostiene la cookie.
      token = 'cookie'; usuario = data.usuario; cliente = data.cliente; suscripcion = data.suscripcion;
      saveSession(token, usuario, cliente, suscripcion);
      initDashboard();
    } else {
      err.textContent=data.mensaje||'Credenciales incorrectas.'; err.style.display='block';
    }
  }catch(e){
    err.textContent = (e.name === 'AbortError') ? 'El servidor tardó demasiado. Reintenta.' : 'Error de conexión.';
    err.style.display='block';
  }
  btn.disabled=false; btn.innerHTML='<i class="ti ti-arrow-right"></i> Ingresar al Portal';
});

['login-email','login-password'].forEach(id=>document.getElementById(id).addEventListener('keydown',e=>{if(e.key==='Enter')document.getElementById('btn-login').click();}));

function initDashboard(){
  if (suscripcion && suscripcion.fecha_vencimiento) {
    const fechaVenc = new Date(suscripcion.fecha_vencimiento);

    // ⚠️ **Acá vivía una cuarta definición del corte por impago, y era la más dañina.** Decía
    // `difDias > 5`: los cinco días de gracia escritos a mano --son `planes.dias_gracia_impago`, una
    // columna por plan justamente para que cambiar los términos sea un UPDATE y no un deploy-- y
    // **sin mirar `cubierta_hasta`**. O sea que a un cliente prepago le cerraba la sesión y lo
    // sacaba del portal seis días después de su vencimiento, estando pagado. No mostraba un dato
    // equivocado: le quitaba el producto.
    //
    // Quién está cortado lo decide `auth.servicio_suspendido` y nadie más (lote 3). Y el portal
    // **no se bloquea**: es donde se lee el motivo y por dónde se paga, así que un cartel que tapa
    // la puerta de salida no cobra más rápido. Lo que sí queda cerrado son las superficies del
    // producto, y eso lo aplica la API, no esta pantalla.
    if (salirDelPortalSiCorresponde()) return;
    showScreen('dashboard-screen');
    mostrarAccesosSiCorresponde();
    const opcionesFecha = { day: '2-digit', month: '2-digit', year: 'numeric' };
    document.getElementById('plan-cobro').textContent = fechaVenc.toLocaleDateString('es-CL', opcionesFecha);

    // El badge lo pinta `cargarServicio()` con lo que dice la base, no restando fechas acá: los
    // días de gracia son del plan y `cubierta_hasta` puede hacer que un vencimiento pasado no sea
    // ninguna deuda. Hasta que llegue esa respuesta se deja lo último conocido.
    cargarServicio();
  } else {
    if (salirDelPortalSiCorresponde()) return;
    showScreen('dashboard-screen');
    mostrarAccesosSiCorresponde();
    document.getElementById('plan-estado').innerHTML = '<span class="badge badge-red"><i class="ti ti-alert-triangle"></i> Inactivo</span>';
    document.getElementById('plan-cobro').textContent = '—';
  }

  // Recien aca: en DOMContentLoaded todavia no hay sesion, y un 401 en el arranque hace que el
  // reintento por /portal/refresh corra sobre la pantalla de acceso para nada.
  cargarAgente();

  // El chip de la cuenta de la cabecera comun apunta a `app.deviant.cl/#perfil`: desde el CRM, el
  // calendario, Marketing o el catalogo no hay editor de perfil, y duplicarlo serian dos
  // formularios que guardan lo mismo. Se limpia el hash para que recargar no lo vuelva a abrir.
  if (location.hash === '#perfil') {
    history.replaceState(null, '', location.pathname + location.search);
    window.openProfile?.();
  }

  const nombreUsuario = usuario.nombre || 'Agente', nombreNegocio = cliente.nombre_negocio || 'Empresa';
  document.getElementById('user-name').textContent = nombreUsuario;
  updateAvatarDisplays();
  // Sin la segunda frase por ahora: la escribe `renderServicio`, que es quien sabe si el sistema
  // está operando de verdad. Afirmarlo acá dejaba «Tu sistema está operando» dos centímetros arriba
  // del cartel que dice que se suspendió.
  document.getElementById('page-subtitle').textContent = `Bienvenido, ${nombreUsuario} de ${nombreNegocio}.`;
  
  const plan = suscripcion && suscripcion.nombre_plan ? suscripcion.nombre_plan : 'Básico';
  document.getElementById('plan-nombre').textContent = plan.charAt(0).toUpperCase() + plan.slice(1);

  if (suscripcion) {
    const planName = (suscripcion.nombre_plan || '').toLowerCase();
    
    const limitBase = suscripcion.limite_base || 0;
    const consBase = suscripcion.consumo_base || 0;
    const limitIA = suscripcion.limite_ia || 0;
    const consIA = suscripcion.consumo_ia || 0;
    const limitAPI = suscripcion.limite_api || 0;
    const consAPI = suscripcion.consumo_api || 0;

    document.getElementById('ui-limite-mensajes').style.display = 'block';
    document.getElementById('txt-mensajes-consumidos').textContent = consBase;
    
    if (limitBase > 0) {
      document.getElementById('txt-mensajes-limite').textContent = limitBase;
      let pctBase = (consBase / limitBase) * 100;
      document.getElementById('txt-mensajes-pct').textContent = Math.round(pctBase) + '%';
      const barMensajes = document.getElementById('bar-mensajes');
      barMensajes.style.width = Math.min(pctBase, 100) + '%';
      if(pctBase >= 90) barMensajes.classList.add('warning');
    } else {
      document.getElementById('txt-mensajes-limite').textContent = '∞';
      document.getElementById('txt-mensajes-pct').textContent = 'Ilimitado';
      const barMensajes = document.getElementById('bar-mensajes');
      barMensajes.style.width = '100%';
      barMensajes.style.background = 'var(--g)';
    }

    if (planName.includes('pro') || planName.includes('api') || planName.includes('enterprise')) {
      document.getElementById('ui-limite-ia').style.display = 'block';
      document.getElementById('txt-ia-consumidos').textContent = consIA;
      
      if (limitIA > 0) {
        document.getElementById('txt-ia-limite').textContent = limitIA;
        let pctIA = (consIA / limitIA) * 100;
        document.getElementById('txt-ia-pct').textContent = Math.round(pctIA) + '%';
        const barIA = document.getElementById('bar-ia');
        barIA.style.width = Math.min(pctIA, 100) + '%';
        if(pctIA >= 90) barIA.classList.add('warning');
      } else {
        document.getElementById('txt-ia-limite').textContent = '∞';
        document.getElementById('txt-ia-pct').textContent = 'Ilimitado';
        const barIA = document.getElementById('bar-ia');
        barIA.style.width = '100%';
        barIA.style.background = 'var(--g)';
      }
    }

    if (planName.includes('api') || planName.includes('enterprise')) {
      document.getElementById('ui-limite-api').style.display = 'block';
      document.getElementById('txt-api-consumidos').textContent = consAPI;
      
      if (limitAPI > 0) {
        document.getElementById('txt-api-limite').textContent = limitAPI;
        let pctAPI = (consAPI / limitAPI) * 100;
        document.getElementById('txt-api-pct').textContent = Math.round(pctAPI) + '%';
        const barAPI = document.getElementById('bar-api');
        barAPI.style.width = Math.min(pctAPI, 100) + '%';
        if(pctAPI >= 90) barAPI.classList.add('warning');
      } else {
        document.getElementById('txt-api-limite').textContent = '∞';
        document.getElementById('txt-api-pct').textContent = 'Ilimitado';
        const barAPI = document.getElementById('bar-api');
        barAPI.style.width = '100%';
        barAPI.style.background = 'var(--g)';
      }
    }
  }
    
  // Los canales van **antes** que el estado: `renderInstances` lee el mapa de instalación al
  // dibujar, y si llega después, la primera pintada sale sin el fragmento y sólo aparece en el
  // sondeo siguiente -- diez segundos en los que el cliente ya se fue a buscarlo a otra parte.
  // La barra lateral se cablea antes del primer pintado: si el usuario aprieta una vista mientras
  // carga, tiene que responder igual.
  document.querySelectorAll('.pv-item[data-vista]').forEach(b =>
    b.addEventListener('click', () => irAVista(b.dataset.vista)));
  // El logo vuelve a Inicio. `irAVista` ya deja el scroll arriba, que es la otra mitad de lo que
  // alguien espera al tocar el logo de una app.
  document.getElementById('nav-logo')?.addEventListener('click', () => irAVista('inicio'));
  // El acceso de Inicio abre el mismo cajón que el icono del encabezado: un segundo camino a la
  // misma función, no una función nueva.
  document.getElementById('pv-abrir-mensajes')?.addEventListener('click',
    () => document.getElementById('btn-abrir-mensajes')?.click());

  // Los dos ámbitos y el redibujado cruzado viven dentro del botón.
  montarBotonTema(document.getElementById('tema-boton'));

  const vistaInicial = location.hash.replace('#', '');
  irAVista(['inicio','canales','asistente','metricas','plan'].includes(vistaInicial)
    ? vistaInicial : 'inicio');

  // ⚠️ El orden importa dos veces. Los canales van antes que el estado porque `renderInstances`
  // lee el mapa de instalación al dibujar; y **las métricas van después del estado**, porque el
  // canal activo lo resuelve `renderInstances`: pedidas antes, la primera pintada sale de toda la
  // cuenta mientras el selector ya marca un canal, que es la misma mentira que se acaba de
  // arreglar, sólo que durante los primeros segundos.
  fetchCanales().then(fetchEstado).then(() => fetchMetricas({ mostrarCarga: true }));
  fetchNotificaciones();
  fetchDerivaciones();
  fetchActividad();
  fetchVentana();
  fetchPagos();
  clearInterval(refreshInterval);
  refreshInterval = setInterval(() => {
    fetchEstado(false);
    fetchNotificaciones();
    // Entra al ciclo corto y no a uno propio: alguien pidió hablar con una persona y está esperando
    // ahora. Diez segundos es lo mismo que tarda el estado de la línea, y no agrega una entrada más
    // que alguien tenga que recordar.
    fetchDerivaciones();
  }, 10000);
  // Las métricas del mes no cambian cada 10 s y cada consulta es un reporte de
  // Chatwoot: se refrescan cada 5 min, no en el ciclo corto.
  setInterval(fetchMetricas, 300000);
  document.getElementById('refresh-dot').classList.add('active');
}

// conSkeleton en false para los refrescos: reemplazar la tarjeta por el skeleton en
// cada actualización hace parpadear la pantalla entera. En el refresco manual el aviso
// visual lo da el botón que gira, no el contenedor.
async function fetchEstado(conSkeleton = true){
  const container = document.getElementById('instances-container');

  // INYECCIÓN DE SKELETON LOADER SI EL CONTENEDOR NO TIENE TARJETAS AÚN
  if (conSkeleton && !container.innerHTML.includes('instance-header')) {
    container.innerHTML = `
      <div class="card skeleton">
        <div class="skeleton__block skeleton__block--title"></div>
        <div class="skeleton__block skeleton__block--text"></div>
        <div class="skeleton__block skeleton__block--text"></div>
        <div style="margin-top:24px;" class="skeleton__block skeleton__block--title"></div>
      </div>
    `;
  }

  let data;
  try{
    data = await apiFetch(API.estado, {
      headers: {'Authorization':'Bearer '+token},
      hideErrorToast: true
    });
  }catch(e){
    if(e.status === 401){
      clearSession();clearInterval(refreshInterval);showScreen('login-screen');return;
    }
    // La API dice a dónde pertenece este usuario. Llega por un enlace guardado o por una sesión
    // abierta antes de que le cambiaran el rol.
    if(e.status === 403 && e.cuerpo && e.cuerpo.destino === 'agenda'){
      clearInterval(refreshInterval); location.replace('./agenda.html'); return;
    }
    // ⚠️ Servicio cortado por impago. Esta ruta es de las que el corte cierra, así que sin este
    // caso el cliente veía «Error de Red» -- el síntoma de una falla nuestra -- justo cuando la
    // causa es que no pagó, y llamaría a soporte en vez de pagar. El aviso se pide aparte, a una
    // ruta que el corte NO cierra a propósito.
    if(e.status === 403 && e.cuerpo && e.cuerpo.motivo === 'impago'){
      await cargarServicio();
      container.innerHTML = '';
      return;
    }
    console.error("Error consultando canales relacionales", e);
    // Solo el fallo de red llega acá. El pintado va fuera del try a propósito: cuando
    // estaba dentro, un ReferenceError de renderInstances se mostraba como caída de red
    // y mandaba a revisar el backend, que estaba respondiendo 200.
    container.innerHTML = `<div class="card"><div class="status-indicator"><div class="status-dot disconnected"></div><div><div class="status-text">Error de Red</div><div class="status-detail">No se pudo consultar el estado de la línea. Reintentando...</div></div></div></div>`;
    return;
  }

  if(data.success) renderServicio(data.servicio);
  if(data.success && data.instancias) renderInstances(data.instancias);
}

// Aviso de servicio cortado por impago.
//
// Existe porque el corte lo hacemos nosotros: pasados los días de gracia del plan, el bot deja de
// contestar. Sin este cartel el cliente ve la línea conectada y al bot mudo, que es exactamente el
// síntoma de una falla nuestra — y nos llamaría por soporte en vez de pagar.
//
// El texto dice qué pasa, desde cuándo y cómo se arregla, en ese orden. No se ofrece un botón de
// pago porque todavía no hay pasarela encendida (Mercado Pago está decidido y sin código): prometer
// acá un botón que no existe sería peor que mandar a hablar con nosotros.
// Se pregunta por su cuenta y no dentro de `/portal/estado`: esa ruta la cierra el corte, así que
// el aviso que explica el corte moría con él.
async function cargarServicio() {
  try {
    const r = await apiFetch(API.servicio, { hideErrorToast: true });
    if (r?.success) renderServicio(r.servicio);
  } catch { /* sin estado, no se dibuja nada: un cartel a medias es peor que ninguno */ }
}

// El badge del plan, con una entrada por estado. Antes eran dos ramas calculadas acá con los cinco
// días de gracia escritos a mano y sin mirar `cubierta_hasta`: un cliente prepago hasta diciembre
// veía «Por pagar».
const BADGE_COBRO = {
  al_dia:          () => '<span class="badge badge-green"><i class="ti ti-check"></i> Al día</span>',
  cubierto:        (s) => `<span class="badge badge-green"><i class="ti ti-check"></i> Cubierto${s.cubierta_hasta ? ' al ' + String(s.cubierta_hasta).slice(8, 10) + '-' + String(s.cubierta_hasta).slice(5, 7) : ''}</span>`,
  por_vencer:      (s) => `<span class="badge badge-orange"><i class="ti ti-clock"></i> Vence en ${s.dias}d</span>`,
  en_gracia:       (s) => `<span class="badge badge-orange"><i class="ti ti-clock"></i> Por pagar (${Math.max(0, (s.dias_gracia ?? 0) + (s.dias ?? 0) + 1)}d de gracia)</span>`,
  cortado:         () => '<span class="badge badge-red"><i class="ti ti-alert-triangle"></i> Suspendido</span>',
  sin_vencimiento: () => '<span class="badge badge-orange"><i class="ti ti-alert-triangle"></i> Sin fecha de cobro</span>',
  sin_plan:        () => '<span class="badge badge-red"><i class="ti ti-alert-triangle"></i> Inactivo</span>',
};

function renderServicio(servicio) {
  const badge = document.getElementById('plan-estado');
  // Un estado que este mapa no conozca sale con su propio nombre en vez de desaparecer: feo,
  // cierto, y visiblemente una omisión. Mismo criterio que `comun/eventos.js`.
  if (badge && servicio?.estado) {
    badge.innerHTML = (BADGE_COBRO[servicio.estado] ?? (() =>
      `<span class="badge badge-orange">${servicio.estado.replace(/_/g, ' ')}</span>`))(servicio);
  }

  // La frase de la cabecera la decide el mismo estado, por lo mismo.
  const sub = document.getElementById('page-subtitle');
  if (sub && !/operando|suspendido/.test(sub.textContent)) {
    sub.textContent += servicio?.suspendido ? ' El servicio está suspendido.' : ' Tu sistema está operando.';
  }

  const caja = document.getElementById('ui-aviso-servicio');
  if (!caja) return;
  if (!servicio?.suspendido) { caja.innerHTML = ''; caja.hidden = true; return; }
  // ⚠️ Diez caracteres. `fecha_vencimiento` es una columna `date` devuelta por una consulta cruda,
  // así que el driver la convierte en Date y llega como '2026-08-20T00:00:00.000Z': concatenarle
  // 'T00:00:00' daba `Invalid Date` y el cartel salía sin fecha. Ver CLAUDE.md.
  const iso = servicio.vence ? String(servicio.vence).slice(0, 10) : null;
  const desde = iso
    ? new Date(iso + 'T00:00:00').toLocaleDateString('es-CL',
        { day: 'numeric', month: 'long', year: 'numeric' })
    : null;
  caja.hidden = false;
  caja.innerHTML = `
    <div class="card aviso-suspendido">
      <div class="status-indicator">
        <div class="status-dot disconnected"></div>
        <div>
          <div class="status-text">Servicio suspendido por falta de pago</div>
          <div class="status-detail">
            El asistente dejó de responder mensajes${desde ? `: la suscripción venció el ${desde}` : ''}.
            La línea sigue conectada y no se perdió ninguna conversación.
            Al registrarse el pago, el servicio se reactiva de inmediato.
          </div>
        </div>
      </div>
    </div>`;
}

// Actividad del mes (2.5). Si Chatwoot no contesta, el bloque simplemente no se muestra:
// un panel de métricas en blanco confunde menos que uno lleno de ceros que parecen datos.
async function fetchMetricas({ mostrarCarga = false } = {}) {
  const caja = document.getElementById('ui-metricas-mes');
  if (!caja) return;
  // ⚠️ La carga se muestra **sólo cuando alguien hizo algo**, no en el refresco automático de cada
  // 5 minutos: un panel que parpadea solo, sin que nadie lo toque, se lee como un error.
  const analitica = document.getElementById('ui-analitica');
  if (mostrarCarga) {
    analitica?.classList.add('cargando');
    caja.classList.add('cargando');
  }
  try {
    // Mismo criterio que la actividad: el filtro va al servidor. Chatwoot sabe responder por inbox
    // (`type=inbox&id=N`), así que el selector de canal filtra de verdad y no de adorno.
    const filtro = instanciaActiva && instanciasCache.length > 1
      ? '?instancia_id=' + encodeURIComponent(instanciaActiva)
      : '';
    const data = await apiFetch(API.metricas + filtro, {
      headers: { 'Authorization': 'Bearer ' + token },
      hideErrorToast: true
    });
    // La analitica de 7 dias (2.16) va antes del bloque del mes y por separado: si
    // Chatwoot no devolvio el resumen mensual, la serie de la semana igual puede tener
    // datos, y perderla por un `return` de otra seccion seria gratis.
    if (data) pintarAnalitica(data.analitica, data.canal_filtrado);

    if (!data || !data.mes) { caja.style.display = 'none'; return; }
    const m = data.mes;
    const nombreMes = new Date(m.desde + 'T00:00:00').toLocaleDateString('es-CL', { month: 'long' });
    // ⚠️ El selector vive en Métricas y estas cifras están en Plan: si no se dice de qué canal son,
    // el cliente lee "Actividad de agosto" creyendo que es de todo el negocio cuando puede ser de
    // un solo canal. El título lo dice.
    const canal = data.canal_filtrado
      ? instanciasCache.find(i => i.id === instanciaActiva) : null;
    document.getElementById('met-mes').textContent = canal
      ? `${nombreMes} · sólo ${telefonoLegible(canal.numero) || datosCanal(canal.tipo_canal)[1]}`
      : nombreMes;
    document.getElementById('met-conversaciones').textContent = m.conversaciones;
    document.getElementById('met-recibidos').textContent = m.mensajes_recibidos.toLocaleString('es-CL');
    document.getElementById('met-enviados').textContent = m.mensajes_enviados.toLocaleString('es-CL');
    document.getElementById('met-resueltas').textContent = m.resueltas;
    const s = m.primera_respuesta_seg;
    document.getElementById('met-respuesta').textContent =
      !s ? '—' : (s < 60 ? `${s} s` : `${Math.round(s / 60)} min`);

    // uptime null = todavía no hay historial suficiente. Se oculta en vez de mostrar
    // un 100 % que nadie podría desmentir.
    const cajaUp = document.getElementById('ui-uptime');
    if (data.uptime_7d === null || data.uptime_7d === undefined) {
      cajaUp.style.display = 'none';
    } else {
      document.getElementById('met-uptime').textContent = data.uptime_7d + '%';
      cajaUp.style.display = 'flex';
    }
    caja.style.display = 'block';
  } catch (e) {
    caja.style.display = 'none';
  } finally {
    // Pase lo que pase se apaga: un indicador de carga que se queda encendido dice que el
    // sistema esta colgado cuando en realidad la peticion fallo y ya termino.
    analitica?.classList.remove('cargando');
    caja.classList.remove('cargando');
  }
}

// Registro de actividad (2.2 y 2.18). La misma lista sirve para las dos fichas: el
// control del bot quiere las últimas acciones y la auditoría quiere el historial, así
// que cambia el largo, no la vista.
//
// Cómo se lee cada fila está en `comun/eventos.js`, compartido con el panel: las dos pantallas
// muestran los mismos eventos y las dos mostraban «null» en los que no escribe la API.

function fechaEvento(iso) {
  // ⚠️ Sin esta guarda, `new Date(null)` es el epoch y un pago **pendiente** --que por definicion
  // tiene `pagado_en` en NULL-- se dibujaba «1/1, 12:00 a. m.» en el historial de facturacion. Una
  // fecha de 1970 al lado de un monto no se lee como "todavia no": se lee como un error nuestro,
  // justo en la pantalla que alguien mira cuando discute un cobro.
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('es-CL', {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'
  });
}

// Ventana de respuesta de 24 h (3.9). La abre el ultimo mensaje ENTRANTE: es el cliente
// final el que habilita a contestarle. Contarla desde el ultimo mensaje cualquiera la
// dejaria abierta para siempre con solo escribir, que es lo contrario de la regla.
//
// Solo se dibuja si hay algo que decir. Un cartel fijo que casi siempre dice "todo bien"
// deja de leerse justo el dia que dice otra cosa.
async function fetchVentana() {
  const caja = document.getElementById('ui-ventana');
  const detalle = document.getElementById('ventana-detalle');
  if (!caja || !detalle) return;
  try {
    const data = await apiFetch(API.ventana, {
      headers: { 'Authorization': 'Bearer ' + token },
      hideErrorToast: true
    });
    const v = data.ventana;
    if (!v || !v.fuera_de_ventana) { caja.style.display = 'none'; return; }

    // `bloqueadas` es distinto de `fuera_de_ventana` a proposito: en una linea de
    // Evolution la ventana no la hace cumplir nadie, asi que avisar "no podes responder"
    // seria falso. En una linea Cloud API si, y ahi hace falta una plantilla aprobada.
    const bloquea = v.bloqueadas > 0;
    detalle.innerHTML = `
      <div style="display:flex; gap:8px; align-items:flex-start;">
        <i class="ti ti-clock" style="color:${bloquea ? '#e05a5a' : 'var(--off)'};"></i>
        <div>
          <strong>${v.fuera_de_ventana}</strong> conversación(es) sin respuesta del cliente
          hace más de 24 h.
          ${bloquea
            ? `<div style="margin-top:4px;">De esas, <strong>${v.bloqueadas}</strong> están en la
               línea oficial de WhatsApp: para escribirles hace falta una plantilla aprobada.</div>`
            : `<div style="margin-top:4px; opacity:.75;">Tu línea no usa la API oficial, así que
               podés seguir respondiendo con normalidad. Es solo para que lo tengas presente.</div>`}
        </div>
      </div>`;
    caja.style.display = 'block';
  } catch (e) {
    // Sin datos no se inventa un estado: simplemente no se muestra el bloque.
    caja.style.display = 'none';
  }
}

async function fetchActividad(limite = 5) {
  const caja = document.getElementById('ui-actividad');
  const lista = document.getElementById('actividad-lista');
  if (!caja || !lista) return;
  try {
    // Se pide filtrada por la instancia activa (2.15). El filtro va en el servidor:
    // recortar despues de traer 5 filas devolveria menos de 5 -- o ninguna -- para un
    // canal que si tuvo actividad.
    const filtro = instanciaActiva && instanciasCache.length > 1
      ? '&instancia_id=' + encodeURIComponent(instanciaActiva)
      : '';
    const data = await apiFetch(API.actividad + '?limite=' + limite + filtro, {
      headers: { 'Authorization': 'Bearer ' + token },
      hideErrorToast: true
    });
    if (!data || !data.eventos || data.eventos.length === 0) {
      caja.style.display = 'none';
      return;
    }
    // ⚠️ Estas filas reusaban `.limit-stats` --la clase de los contadores de consumo: 12 px, en
    // monoespaciada y en el gris más apagado-- con seis estilos en línea encima. Servía para un
    // "0 / ∞" y no para un registro que se lee: el resultado era letra chica, apretada y sin
    // jerarquía. Ahora tiene sus propias clases, y el icono queda dentro de un círculo, que es lo
    // que separa una fila de la siguiente sin necesidad de una línea.
    lista.innerHTML = data.eventos.map(e => `
      <div class="act-fila">
        <span class="act-icono"><i class="ti ${iconoEvento(e.tipo, e.detalle)}"></i></span>
        <span class="act-txt">
          <span class="act-que">${esc(textoEvento(e.tipo, e.detalle))}</span>
          <span class="act-quien">${esc(e.usuario || 'Sistema')}${e.numero_telefono ? ' · ' + esc(telefonoLegible(e.numero_telefono)) : ''}</span>
        </span>
        <span class="act-fecha">${fechaEvento(e.creado_en)}</span>
      </div>
    `).join('');
    caja.style.display = 'block';
  } catch (e) {
    caja.style.display = 'none';
  }
}

// La auditoría completa es la misma lista, más larga. No hace falta otra pantalla para
// mostrar lo mismo con otro título.
window.verMasActividad = async function(btn) {
  btn.disabled = true;
  try {
    await fetchActividad(50);
    btn.style.display = 'none';
  } finally {
    btn.disabled = false;
  }
};

// Dashboard analitico (2.16).
//
// Tres piezas, y cada una es la forma que le corresponde al dato: siete dias de
// actividad son magnitudes a comparar entre si (barras agrupadas), la distribucion
// horaria es una distribucion (barras finas y continuas), y los tiempos de respuesta
// son tres magnitudes sueltas con su comparacion -- eso es un numero, no un grafico.
//
// Lo que la ficha pide y NO esta: resolucion por IA y temas. No se pueden calcular hoy
// y el motivo esta escrito en la pantalla, no escondido acá: la IA responde a traves
// del mismo usuario de Chatwoot que una persona (no hay agent_bot registrado) y no
// existe clasificacion de temas en el stack. Un panel con una cifra inventada es peor
// que un panel mas chico -- es el error del modal de administrar cliente otra vez.

function duracion(segundos) {
  if (segundos == null) return null;
  const s = Math.round(Number(segundos));
  if (!isFinite(s) || s < 0) return null;
  if (s < 60)    return s + ' s';
  if (s < 3600)  return Math.round(s / 60) + ' min';
  if (s < 86400) return (s / 3600).toFixed(1).replace('.0', '') + ' h';
  return Math.round(s / 86400) + ' d';
}

/**
 * Comparacion contra la ventana anterior. `menosEsMejor` porque en un tiempo de
 * respuesta bajar es mejorar, y en conversaciones resueltas es al reves: pintar las
 * dos con la misma regla diria que empeoro justo cuando mejoro.
 */
function delta(actual, previo, menosEsMejor) {
  if (actual == null || previo == null || Number(previo) === 0) return '';
  const cambio = ((Number(actual) - Number(previo)) / Number(previo)) * 100;
  if (!isFinite(cambio) || Math.abs(cambio) < 1) return '';
  const mejoro = menosEsMejor ? cambio < 0 : cambio > 0;
  const signo = cambio > 0 ? '+' : '';
  return `<span class="g-delta ${mejoro ? 'mejor' : 'peor'}">${signo}${Math.round(cambio)}%</span>`;
}

const DIA_CORTO = ['do', 'lu', 'ma', 'mi', 'ju', 'vi', 'sa'];

function pintarDias(dias) {
  const caja = document.getElementById('g-dias');
  if (!caja) return;
  // La escala la fija el maximo de LAS DOS series juntas: una escala por serie haria
  // que dos alturas iguales significaran numeros distintos.
  const tope = Math.max(1, ...dias.map(d => Math.max(d.entrantes, d.salientes)));
  caja.innerHTML = dias.map(d => {
    // La fecha viene YYYY-MM-DD; se parte a mano para que no la corra la zona horaria.
    const [a, m, dd] = d.fecha.split('-').map(Number);
    const fecha = new Date(a, m - 1, dd);
    const alto = (v) => Math.max(2, Math.round((v / tope) * 100)) + '%';
    return `
      <div class="g-dia">
        <div class="g-par">
          <div class="g-barra" style="height:${alto(d.entrantes)};background:var(--serie-1)"
               title="${d.fecha}: ${d.entrantes} recibidos"></div>
          <div class="g-barra" style="height:${alto(d.salientes)};background:var(--serie-2)"
               title="${d.fecha}: ${d.salientes} enviados"></div>
        </div>
        <div class="g-etiqueta">${DIA_CORTO[fecha.getDay()]} ${dd}</div>
      </div>`;
  }).join('');
}

function pintarHoras(horas) {
  const caja = document.getElementById('g-horas');
  const nota = document.getElementById('g-horas-nota');
  if (!caja) return;
  const tope = Math.max(...horas.map(h => h.mensajes));
  if (tope === 0) {
    caja.innerHTML = '';
    nota.textContent = 'Todavia no hay mensajes suficientes para ver un patron.';
    return;
  }
  caja.innerHTML = horas.map(h => {
    const alto = Math.max(2, Math.round((h.mensajes / tope) * 100));
    return `<div class="g-hora ${h.mensajes === 0 ? 'vacia' : ''}"
                 style="height:${alto}%"
                 title="${String(h.hora).padStart(2, '0')}:00 — ${h.mensajes} mensajes"></div>`;
  }).join('');
  // Etiqueta directa solo del peak: poner el numero en las 24 barras taparia el grafico
  // y no responderia mejor la pregunta, que es "cuando".
  const pico = horas.reduce((a, b) => (b.mensajes > a.mensajes ? b : a));
  nota.innerHTML = `Hora de mayor volumen: <strong>${String(pico.hora).padStart(2, '0')}:00</strong>
                    (${pico.mensajes} mensajes en 7 dias, hora de Chile).`;
}

function pintarRespuesta(r) {
  const caja = document.getElementById('g-respuesta');
  if (!caja) return;
  const filas = [
    ['Primera respuesta', duracion(r.primera_seg), delta(r.primera_seg, r.primera_seg_previo, true)],
    ['Tiempo hasta resolver', duracion(r.resolucion_seg), delta(r.resolucion_seg, r.resolucion_seg_previo, true)],
    ['Conversaciones resueltas', r.resueltas != null ? String(r.resueltas) : null,
     delta(r.resueltas, r.resueltas_previo, false)]
  ];
  caja.innerHTML = filas.map(([etiqueta, valor, cambio]) => `
    <div class="g-tarjeta">
      <span class="g-tarjeta-label">${etiqueta}</span>
      <span class="g-tarjeta-valor">${valor ?? '—'}${valor ? cambio : ''}</span>
    </div>`).join('');
}

// Resolucion por IA (2.16). Es la metrica NATIVA de Chatwoot, no una nuestra: cuenta
// conversaciones resueltas sin que ninguna persona haya escrito. Existe desde que el
// agente firma con su propio token de AgentBot -- antes la IA y una persona eran el
// mismo remitente y el numero habria sido inventado.
function pintarIA(ia) {
  const caja = document.getElementById('g-ia');
  const nota = document.getElementById('g-ia-nota');
  if (!caja || !ia) return;
  const total = (Number(ia.resueltas) || 0) + (Number(ia.traspasos) || 0);
  const pct = total > 0 ? Math.round((Number(ia.resueltas) / total) * 100) : null;

  caja.innerHTML = `
    <div class="g-tarjeta">
      <span class="g-tarjeta-label">Resueltas por la IA sola</span>
      <span class="g-tarjeta-valor">${ia.resueltas ?? 0}${pct !== null ? ` <span class="g-delta">${pct}%</span>` : ''}</span>
    </div>
    <div class="g-tarjeta">
      <span class="g-tarjeta-label">Pasaron a una persona</span>
      <span class="g-tarjeta-valor">${ia.traspasos ?? 0}</span>
    </div>`;

  // Decir desde cuando se mide no es un detalle: la metrica no es retroactiva, y sin
  // esta linea un cero se lee como "la IA no resuelve nada" en vez de "todavia no hay
  // suficientes conversaciones desde que se empezo a medir".
  const desde = new Date(ia.mide_desde + 'T12:00:00').toLocaleDateString('es-CL',
    { day: 'numeric', month: 'long' });
  nota.innerHTML = total === 0
    ? `Todavía no hay conversaciones cerradas desde que se empezó a medir (${desde}).`
    : `Medido sobre conversaciones cerradas desde el ${desde}.`;
}

// Temas (2.16). Los produce el agente de n8n: clasifica cada mensaje entrante en uno de
// siete temas y etiqueta la conversacion en Chatwoot. La lista cerrada es deliberada --
// temas libres darian cientos de etiquetas distintas y un panel ilegible.
//
// Barras horizontales y no una torta: son magnitudes a comparar entre si, y una torta
// con siete porciones obliga a comparar angulos, que es justo lo que peor hace el ojo.
const NOMBRE_TEMA = {
  precios:  'Precios y planes',
  horarios: 'Horarios',
  reserva:  'Reservas',
  soporte:  'Soporte',
  reclamo:  'Reclamos',
  despacho: 'Envios',
  otro:     'Otros'
};

function pintarTemas(temas) {
  const caja = document.getElementById('g-temas');
  const nota = document.getElementById('g-temas-nota');
  if (!caja) return;

  if (!temas || temas.length === 0) {
    caja.innerHTML = '';
    nota.textContent = 'Todavía no hay conversaciones clasificadas en esta ventana.';
    return;
  }

  const tope = Math.max(...temas.map(t => t.conversaciones));
  const total = temas.reduce((a, t) => a + t.conversaciones, 0);
  caja.innerHTML = temas.map(t => {
    const ancho = Math.max(2, Math.round((t.conversaciones / tope) * 100));
    return `
      <div style="margin-bottom:12px;">
        <div class="limit-stats" style="margin-bottom:4px;">
          <span>${NOMBRE_TEMA[t.tema] || t.tema}</span>
          <span>${t.conversaciones}</span>
        </div>
        <div class="limit-bar-bg">
          <div class="limit-bar-fill" style="width:${ancho}%; background:var(--serie-1);"></div>
        </div>
      </div>`;
  }).join('');
  nota.textContent = `${total} conversaciones clasificadas en los últimos 7 días.`;
}

function pintarAnalitica(analitica, canalFiltrado) {
  const caja = document.getElementById('ui-analitica');
  if (!caja || !analitica) return;
  // El reporte de etiquetas de Chatwoot no acepta alcance por inbox: con un canal elegido, esta
  // tarjeta sigue siendo de toda la cuenta. Se dice, en vez de dejar que se lea como del canal.
  const nota = document.getElementById('g-temas-alcance');
  if (nota) {
    nota.textContent = canalFiltrado && analitica.temas_por_canal === false
      ? 'Este desglose es de todos tus canales juntos.' : '';
  }
  pintarDias(analitica.dias || []);
  pintarHoras(analitica.horas || []);
  pintarRespuesta(analitica.respuesta || {});
  pintarIA(analitica.ia);
  pintarTemas(analitica.temas);
  caja.style.display = 'grid';
}

// Historial de pagos (2.3). La ficha pedía "plan, próximo cobro y estado de cuenta":
// lo primero ya estaba, esto es lo que faltaba. Hasta el 31 jul 2026 `pagos` tenía cero
// filas y nada la escribía, así que no había historial que mostrar.
const ETIQUETA_ESTADO_PAGO = {
  confirmado:  ['badge-green', 'Pagado'],
  pendiente:   ['badge-orange', 'Pendiente'],
  fallido:     ['badge-red', 'Rechazado'],
  reembolsado: ['badge-red', 'Reembolsado']
};

// El origen se muestra solo cuando aporta: al cliente le sirve saber que un pago lo
// cargó la agencia a mano y no su tarjeta. 'importado' se dice igual que 'manual'
// porque para el cliente es lo mismo: no lo pagó por el portal.
// No hay entrada de pasarela: Webpay se eliminó el 20 ago 2026 y Mercado Pago todavía no escribe
// pagos. La que venga se agrega acá junto con su ruta, no antes — una etiqueta para un origen que
// nadie puede escribir es una promesa de pantalla.
const ORIGEN_PAGO = {
  manual:      'Transferencia',
  importado:   'Transferencia',
  // Desde 6.1 sí hay pasarela y sí escribe pagos. Se dice «Tarjeta» y no «Mercado Pago»: el cliente
  // compra un producto, no un stack, y el nombre del proveedor no le dice nada que le sirva.
  mercadopago: 'Tarjeta'
};

// El fee de instalación se cobra una sola vez y no corre la fecha de vencimiento. Si
// no se distinguiera del resto, el cliente vería dos cobros el mismo mes sin
// explicación. La mensualidad no se etiqueta: es el caso normal.
const CONCEPTO_PAGO = { setup: 'Instalación' };

function montoPago(monto, moneda) {
  const n = Number(monto);
  if (!isFinite(n)) return '—';
  // CLP no lleva decimales y USD sí. Sin esto un pago chileno se muestra como
  // "119.000,00", que no es como lo escribe nadie acá.
  const decimales = moneda === 'CLP' ? 0 : 2;
  return n.toLocaleString('es-CL', {
    minimumFractionDigits: decimales,
    maximumFractionDigits: decimales
  }) + ' ' + (moneda || '');
}

async function fetchPagos() {
  const caja = document.getElementById('ui-pagos');
  const lista = document.getElementById('pagos-lista');
  if (!caja || !lista || !VE_FACTURACION()) return;
  try {
    const data = await apiFetch(API.pagos, {
      headers: { 'Authorization': 'Bearer ' + token },
      hideErrorToast: true
    });
    // El botón de pagar se enciende con la misma respuesta: lo dibuja la API y no una constante
    // del bundle, así que apagar la pasarela lo saca de la pantalla sin desplegar el frontend.
    pintarPagoTarjeta(data?.mp_activo === true);
    if (!data || !data.pagos || data.pagos.length === 0) {
      caja.style.display = 'none';
      return;
    }
    lista.innerHTML = data.pagos.map(p => {
      const [clase, texto] = ETIQUETA_ESTADO_PAGO[p.estado] || ['badge-orange', p.estado];
      const concepto = CONCEPTO_PAGO[p.concepto] ? CONCEPTO_PAGO[p.concepto] + ' · ' : '';
      // Los dos registros del cliente se leen igual a propósito: son la misma clase de lista
      // --qué pasó y cuándo-- y darles dos formas distintas obliga a aprender dos.
      return `
      <div class="act-fila">
        <span class="act-icono"><i class="ti ti-receipt"></i></span>
        <span class="act-txt">
          <span class="act-que">${montoPago(p.monto, p.moneda)}</span>
          <span class="act-quien">${concepto}${ORIGEN_PAGO[p.origen] || p.origen}${p.referencia_pago ? ' · ' + p.referencia_pago : ''}</span>
        </span>
        <span class="act-fecha">
          <span class="badge ${clase}">${texto}</span>
          <span class="act-fecha-txt">${fechaEvento(p.pagado_en)}</span>
        </span>
      </div>`;
    }).join('');
    caja.style.display = 'block';
  } catch (e) {
    // Un 403 llega acá cuando el rol no es dueño. No es un error que valga un toast:
    // la sección simplemente no le corresponde.
    caja.style.display = 'none';
  }
}

/**
 * El bloque de pagar con tarjeta (6.1).
 *
 * ⚠️ **No cobra acá: manda al checkout de Mercado Pago.** Los datos de la tarjeta nunca tocan un
 * dominio nuestro, que es lo que deja PCI fuera de alcance — y por eso el botón navega en vez de
 * abrir un formulario propio.
 */
function pintarPagoTarjeta(activo) {
  const caja = document.getElementById('ui-pago-tarjeta');
  if (!caja) return;
  caja.hidden = !activo;
  if (!activo) return;
  const boton = document.getElementById('btn-pagar');
  const nota = document.getElementById('pago-tarjeta-nota');
  if (nota) {
    nota.textContent = 'Te llevamos al pago seguro. El cobro se acredita solo, sin comprobante.';
  }
  if (!boton || boton.dataset.listo === '1') return;
  boton.dataset.listo = '1';
  boton.addEventListener('click', async () => {
    boton.disabled = true;
    try {
      const r = await apiFetch(API.pagoTarjeta, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: '{}',
        hideErrorToast: true
      });
      if (!r?.url) throw new Error('No pudimos abrir el pago.');
      // `location.href` y no `window.open`: un pop-up lo bloquea el navegador en cuanto la llamada
      // a la API mete un `await` en el medio, y el clic deja de contar como gesto del usuario.
      window.location.href = r.url;
    } catch (e) {
      Toast.error(e?.mensaje || 'No pudimos abrir el pago. Intenta de nuevo.');
      boton.disabled = false;
    }
  });
}

// "Último ping": lo que Evolution sabe de la última actividad de la línea. Se muestra
// en relativo porque un timestamp absoluto no responde la pregunta que se hace el
// usuario, que es "¿está viva ahora?".
function textoUltimoPing(iso) {
  if (!iso) return 'Sin señal registrada';
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return 'Sin señal registrada';
  const min = Math.floor(ms / 60000);
  if (min < 1)  return 'Último ping: hace instantes';
  if (min < 60) return `Último ping: hace ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24)   return `Último ping: hace ${h} h`;
  return `Último ping: hace ${Math.floor(h / 24)} d`;
}

// Refresco manual. El botón antes llamaba a fetchEstado() y no pasaba nada visible:
// la petición salía, pero el usuario no tenía forma de saberlo y volvía a apretar.
// Ahora el ícono gira y el botón queda deshabilitado mientras dura, con un piso de
// 600 ms para que el cambio se alcance a ver aunque la API conteste en 60 ms.
window.refrescarEstado = async function(btn) {
  const icono = btn.querySelector('i');
  btn.disabled = true;
  icono.classList.add('girando');
  try {
    await Promise.all([fetchEstado(false), new Promise(r => setTimeout(r, 600))]);
    Toast.success('Estado actualizado');
  } finally {
    btn.disabled = false;
    icono.classList.remove('girando');
  }
};

// Reconexión de verdad: reinicia la sesión de Baileys en Evolution. Es distinto del
// refresco, que solo vuelve a preguntar.
window.reconectarInstancia = async function(id, btn) {
  const icono = btn.querySelector('i');
  btn.disabled = true;
  icono.classList.add('girando');
  try {
    const data = await apiFetch(API.reconectar, {
      method: 'POST',
      headers: { 'Authorization': 'Bearer ' + token, 'Content-Type': 'application/json' },
      body: JSON.stringify({ instancia_id: id })
    });
    if (data && data.success) {
      Toast.success('Reconexión solicitada. La línea puede tardar unos segundos.');
      // La sesión tarda en levantar: se relee después, no en el mismo instante.
      setTimeout(() => fetchEstado(false), 4000);
    } else {
      Toast.error((data && data.mensaje) || 'No se pudo reconectar.');
    }
  } catch (e) {
    if (e.status === 401) { clearSession(); showScreen('login-screen'); return; }
    if (e.status === 403) { Toast.error('Tu rol no permite reconectar la línea.'); return; }
    Toast.error('No se pudo reconectar.');
  } finally {
    btn.disabled = false;
    icono.classList.remove('girando');
  }
};

// Desvincular el número para conectar otro. Es lo que reconectar NO hace: `/instance/restart`
// levanta la sesión con las credenciales guardadas, o sea siempre el mismo número, así que con
// la línea arriba no había forma de cambiarlo desde acá -- había que pedírnoslo.
//
// Se confirma porque deja el negocio sin atender: lo que se pregunta es lo que cuesta, no
// «¿estás seguro?». Y se dice que las conversaciones se conservan, porque es la duda obvia
// justo antes de apretar: el inbox del Centro de Mensajes es de la línea y no del número, así
// que el historial sigue ahí con el número nuevo.
window.desvincularInstancia = async function(id, btn) {
  if (!window.confirm(
    '¿Desvincular este número? Tu línea deja de atender hasta que escanees el código QR con el '
    + 'número nuevo, y en ese rato el asistente no responde. Las conversaciones que ya están en '
    + 'tu bandeja se conservan.'
  )) return;

  const etiqueta = btn.textContent;
  btn.disabled = true;
  btn.textContent = 'Desvinculando…';
  try {
    const data = await apiFetch(API.desvincular, {
      method: 'POST',
      headers: { 'Authorization': 'Bearer ' + token, 'Content-Type': 'application/json' },
      body: JSON.stringify({ instancia_id: id })
    });
    if (data && data.success) {
      // El QR puede no estar listo todavía: el socket necesita un instante después de cerrar la
      // sesión. Decirlo es la diferencia entre esperar tranquilo y volver a apretar el botón.
      Toast.success(data.qr
        ? 'Número desvinculado. Escaneá el código QR con el número nuevo.'
        : 'Número desvinculado. El código QR aparece en unos segundos.');
      await fetchEstado(false);
    } else {
      Toast.error((data && data.mensaje) || 'No se pudo desvincular el número.');
    }
  } catch (e) {
    if (e.status === 401) { clearSession(); showScreen('login-screen'); return; }
    if (e.status === 403) { Toast.error('Tu rol no permite cambiar el número.'); return; }
    Toast.error('No se pudo desvincular el número.');
  } finally {
    // Tras un desvincular exitoso `fetchEstado` ya repintó la tarjeta y este botón quedó
    // desprendido del documento, así que esto sólo se nota cuando falló -- que es cuando hace
    // falta que el botón vuelva a estar apretable.
    btn.disabled = false;
    btn.textContent = etiqueta;
  }
};

/**
 * Conectar sin escanear (23 oct 2026): el número primero, el código de 8 caracteres después.
 *
 * El número es obligatorio y no es un trámite: WhatsApp sólo emite el código **contra un
 * número** --`requestPairingCode`-- y al pedirlo le manda un aviso a ese teléfono. De ahí que esto
 * sea un formulario y no un botón: un número mal tecleado le suena el celular a un desconocido.
 *
 * El campo se monta una sola vez y se conserva, igual que el del perfil: el modal se abre y se
 * cierra sin destruirse.
 */
let vincInstancia = null;
let telVincular = null;
function vincularTelefono() {
  if (telVincular) return telVincular;
  const caja = document.getElementById('vinc-telefono-caja');
  if (!caja) return null;
  // El aviso se borra al corregir y no al reenviar: si no, «revisá el teléfono» sigue en rojo
  // debajo de un número ya correcto, que es decirle a alguien que se equivocó cuando no.
  telVincular = campoTelefono({ placeholder: '9 1234 5678', al: () => vincularError('') });
  caja.appendChild(telVincular.nodo);
  return telVincular;
}

function vincularError(texto) {
  const caja = document.getElementById('vincular-error');
  if (!caja) return;
  caja.textContent = texto || '';
  caja.style.display = texto ? 'block' : 'none';
}

window.abrirVincular = function(id) {
  vincInstancia = id;
  vincularError('');
  document.getElementById('vincular-modal').classList.add('active');
  vincularTelefono()?.enfocar();
};

window.cerrarVincular = function() {
  document.getElementById('vincular-modal').classList.remove('active');
};

window.cerrarVincularFuera = function(evento) {
  if (evento.target === document.getElementById('vincular-modal')) window.cerrarVincular();
};

window.pedirCodigoVinculacion = async function() {
  const campo = vincularTelefono();
  const numero = campo?.valor();
  if (!numero) { vincularError(MSG_TELEFONO); return; }

  const btn = document.getElementById('btn-vincular-codigo');
  const etiqueta = btn.textContent;
  btn.disabled = true;
  btn.textContent = 'Pidiendo el código…';
  vincularError('');
  try {
    // `hideErrorToast`: los motivos de esta ruta se leen (la línea sigue conectada, el número no
    // se entiende, WhatsApp no contestó) y van en el modal, donde está la decisión. El toast
    // genérico de `apiFetch` los taparía con «error de conexión».
    const data = await apiFetch(API.codigoVinculacion, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ instancia_id: vincInstancia, numero }),
      hideErrorToast: true
    });
    if (data && data.success) {
      window.cerrarVincular();
      Toast.success('Código generado. Escribilo en el teléfono, en Dispositivos vinculados.');
      // El código se dibuja desde el sondeo y no desde esta respuesta: así el que está en
      // pantalla es siempre el último que emitió WhatsApp. Ver `renderInstancias`.
      await fetchEstado(false);
    } else {
      vincularError((data && data.mensaje) || 'No se pudo generar el código.');
    }
  } catch (e) {
    if (e.status === 401) { clearSession(); showScreen('login-screen'); return; }
    if (e.status === 403) { vincularError('Tu rol no permite conectar un número.'); return; }
    vincularError(e.mensaje || 'No se pudo generar el código.');
  } finally {
    btn.disabled = false;
    btn.textContent = etiqueta;
  }
};

// Selector multi-instancia y multi-canal (2.15).
//
// El portal ya iteraba el arreglo y dibujaba una tarjeta por instancia, pero no habia
// contexto activo: con dos lineas conectadas, "el estado", "la actividad reciente" y el
// interruptor del bot eran tres cosas distintas mezcladas en la misma pantalla sin que
// nada dijera de cual se estaba hablando. Hoy hay una sola instancia, asi que la
// ambiguedad todavia no se ve -- y por eso mismo conviene resolverla antes de que se vea.
//
// Con una sola instancia no aparece nada: un selector de un elemento es ruido.
let instanciaActiva = null;
/**
 * Navegación entre vistas. Mismo patrón que el CRM: una vista a la vez, el hash en la URL para
 * poder recargar donde estabas, y **nada se recarga al cambiar de vista** -- todo lo pinta el
 * sondeo que ya corre, así que moverse por el panel no cuesta una consulta.
 */
window.irAVista = function(vista) {
  document.querySelectorAll('.pv-item[data-vista]').forEach(b =>
    b.classList.toggle('activa', b.dataset.vista === vista));
  document.querySelectorAll('.pv-seccion').forEach(s => {
    s.hidden = s.dataset.seccion !== vista;
  });
  if (location.hash !== '#' + vista) history.replaceState(null, '', '#' + vista);
  window.scrollTo(0, 0);
  // La única que pide datos al entrar: es una consulta que la mayoría de las sesiones no abre nunca,
  // y no cambia sola. Se pide una vez; `pintarAccesos` sale solo si ya trajo algo.
  if (vista === 'accesos') cargarAccesos();
};

// ---------------------------------------------------------------------------
// Accesos (29 ago 2026)
// ---------------------------------------------------------------------------
//
// ⚠️ **Esconder el botón no es el permiso.** Quien decide es `auth.usuario_accesos_guardar`, y esta
// pantalla se dibuja para dueño y administrador sólo por prolijidad: quien llame la ruta igual
// recibe 403. Es la misma regla que ya rige el resto del portal.
//
// Las tres superficies son las del portal. **El calendario no está acá**: lo gobierna
// `agenda_usuarios` —con perfil, ficha y sedes— y se muestra al lado, de sólo lectura, con el camino
// a la pantalla donde sí se cambia. El mismo hecho en dos lugares es libre de contradecirse.
const SUPERFICIES = [
  { id: 'panel',     nombre: 'Panel',     nota: 'Canales, asistente, métricas y plan' },
  { id: 'crm',       nombre: 'CRM',       nota: 'Contactos, notas, tareas y la exportación' },
  { id: 'marketing', nombre: 'Marketing', nota: 'Campañas, embudo y gasto' },
];
const PERFIL_AGENDA = {
  admin: 'Calendario: administra', recepcion: 'Calendario: recepción',
  profesional: 'Calendario: profesional', lectura: 'Calendario: sólo lectura',
};

let accesosCargados = false;

/** Las superficies marcadas en una fila, en el orden de `SUPERFICIES`. */
const leerLlaves = (caja) => SUPERFICIES
  .filter((_, i) => caja.querySelectorAll('input[type=checkbox]')[i]?.checked)
  .map((sup) => sup.id);

// Esconder el botón es prolijidad; el permiso lo aplica la API. Se hace igual porque una pantalla
// que sólo sabe decir 403 es peor que no ofrecerla.
function mostrarAccesosSiCorresponde() {
  const b = document.getElementById('pv-item-accesos');
  if (b) b.hidden = !puedeEditarAccesos();
}

function puedeEditarAccesos() {
  return ['dueno', 'administrador'].includes((usuario && usuario.rol) || '');
}

async function cargarAccesos() {
  if (accesosCargados) return;
  const caja = document.getElementById('accesos-cont');
  if (!caja) return;
  try {
    const r = await apiFetch(API.usuarios, { credentials: 'include', hideErrorToast: true });
    accesosCargados = true;
    pintarAccesos(r.usuarios || [], r.yo);
  } catch (e) {
    caja.innerHTML = '<p class="text-dim" style="font-size:13px;">No se pudieron cargar los accesos.</p>';
  }
}

function pintarAccesos(usuarios, yo) {
  const caja = document.getElementById('accesos-cont');
  const editable = puedeEditarAccesos();
  caja.innerHTML = '';

  if (!editable) {
    caja.innerHTML = '<p class="text-dim" style="font-size:13px;">Sólo el dueño o un administrador de la cuenta pueden ver y cambiar los accesos.</p>';
    return;
  }

  for (const u of usuarios) {
    const fila = document.createElement('div');
    fila.className = 'acceso-fila';

    const quien = document.createElement('div');
    quien.className = 'acceso-quien';
    quien.appendChild(Object.assign(document.createElement('strong'), {
      textContent: `${u.nombre} ${u.apellido || ''}`.trim(),
    }));
    const bajo = document.createElement('span');
    bajo.className = 'acceso-sub';
    // El perfil de calendario al lado del rol: el punto de esta pantalla es ver las dos cosas
    // juntas, que es justo lo que antes no existía en ningún lado.
    const partes = [u.email_login, u.rol === 'dueno' ? 'Dueño' : null,
      PERFIL_AGENDA[u.perfil_agenda] || null,
      u.nunca_entro ? 'todavía no define su contraseña' : null].filter(Boolean);
    bajo.textContent = partes.join(' · ');
    quien.appendChild(bajo);
    fila.appendChild(quien);

    const llaves = document.createElement('div');
    llaves.className = 'acceso-llaves';
    // ⚠️ Al dueño no se le tocan los accesos y a uno mismo tampoco: los dos casos los rechaza la
    // función, y dibujar un interruptor que siempre falla es peor que no dibujarlo.
    const intocable = u.rol === 'dueno' || u.id === yo;
    for (const sup of SUPERFICIES) {
      const lab = document.createElement('label');
      lab.className = 'acceso-llave';
      lab.title = sup.nota;
      const chk = document.createElement('input');
      chk.type = 'checkbox';
      chk.checked = (u.accesos || []).includes(sup.id);
      chk.disabled = intocable;
      chk.addEventListener('change', () => guardarAccesos(u, leerLlaves(llaves)));
      lab.appendChild(chk);
      lab.appendChild(Object.assign(document.createElement('span'), { textContent: sup.nombre }));
      llaves.appendChild(lab);
    }
    fila.appendChild(llaves);

    if (!intocable && u.accesos_propios) {
      // Sólo aparece cuando hay algo que deshacer: NULL es «lo que da el rol» y un botón que
      // devuelve a un estado en el que ya estás no dice nada.
      const volver = document.createElement('button');
      volver.type = 'button';
      volver.className = 'btn-sec btn-sm';
      volver.textContent = 'Según su rol';
      volver.title = 'Vuelve a lo que da su rol y deja de tener una excepción propia';
      volver.addEventListener('click', () => guardarAccesos(u, null));
      fila.appendChild(volver);
    }
    caja.appendChild(fila);
  }

  const nota = document.createElement('p');
  nota.className = 'text-dim';
  nota.style.cssText = 'font-size:12px; margin-top:14px; line-height:1.6;';
  nota.textContent = 'Las cuentas creadas desde el calendario entran sólo al calendario. Para darle el panel, el CRM o Marketing, préndele la llave acá.';
  caja.appendChild(nota);
}

/**
 * Guarda las superficies de una cuenta.
 *
 * `accesos` viaja como lista, o `null` para volver a heredar del rol. Los dos significan cosas
 * distintas y el JSON los distingue, así que no se colapsan: `[]` es «no entra a ninguna».
 */
async function guardarAccesos(u, accesos) {
  try {
    await apiFetch(API.usuarioAccesos, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ usuario_id: u.id, accesos }),
      hideErrorToast: true,
    });
    Toast.success('Accesos actualizados.');
  } catch (e) {
    Toast.error(e?.mensaje || 'No se pudo cambiar el acceso.');
  }
  // Se repinta con lo que quedó **en el servidor** y no con lo que el navegador cree, tanto si
  // salió bien como si no: es la regla que ya costó una vez en `toggleBot`, donde la caché se
  // quedaba con el valor anterior y el usuario terminaba dejándolo al revés.
  accesosCargados = false;
  await cargarAccesos();
}

let instanciasCache = [];
/**
 * Lo que cada canal necesita para terminar de instalarse, por id de instancia.
 *
 * Se pide **una vez** y no en cada sondeo: `/portal/estado` se consulta cada 10 s y el fragmento del
 * webchat no cambia nunca -- mandarlo en cada vuelta serian dos kilobytes por segundo para dibujar
 * lo mismo.
 */
let instalacionPorCanal = new Map();

async function fetchCanales() {
  try {
    const r = await apiFetch(API.canales, { credentials: 'include', hideErrorToast: true });
    instalacionPorCanal = new Map((r.canales ?? [])
      .filter(c => c.instalacion)
      .map(c => [c.id, c.instalacion]));
  } catch { /* si falla, el canal se dibuja igual: sin el fragmento, no roto */ }
}

/**
 * El bloque que le dice al cliente qué hacer con su chat web.
 *
 * ⚠️ Un canal creado y sin instalar **se ve exactamente igual que uno funcionando**: el portal dice
 * "conectado" porque el inbox existe, y en el sitio del cliente no hay ningún chat. Por eso esto no
 * es un detalle de presentación: es lo único que distingue las dos situaciones.
 */
function bloqueInstalacion(instanciaId) {
  const fragmento = instalacionPorCanal.get(instanciaId);
  if (!fragmento) return '';
  return `
      <div class="instalacion">
        <div class="instalacion-titulo">
          <i class="ti ti-forms"></i> Pega esto en tu sitio, antes de <code>&lt;/body&gt;</code>
        </div>
        <textarea class="instalacion-codigo" readonly rows="4">${fragmento
          .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</textarea>
        <button class="btn-sec" onclick="copiarInstalacion(this)">
          <i class="ti ti-check"></i> Copiar
        </button>
        <div class="instalacion-nota">Mientras no esté pegado, el chat no aparece en tu sitio
          aunque acá diga «conectado».</div>
      </div>`;
}

window.copiarInstalacion = function(boton) {
  const ta = boton.parentElement.querySelector('.instalacion-codigo');
  ta.select();
  // `navigator.clipboard` no existe fuera de HTTPS ni en algunos navegadores viejos, y el
  // seleccionado ya quedó hecho arriba: si falla, el cliente copia con Ctrl+C y no se queda sin
  // salida.
  navigator.clipboard?.writeText(ta.value)
    .then(() => showToast('Código copiado', 'success'))
    .catch(() => showToast('Selecciónalo y copia con Ctrl+C', 'error'));
};

const CANAL = {
  whatsapp:          ['ti-brand-whatsapp', 'WhatsApp'],
  whatsapp_business: ['ti-brand-whatsapp', 'WhatsApp API'],
  instagram:         ['ti-brand-instagram', 'Instagram'],
  messenger:         ['ti-brand-messenger', 'Messenger'],
  telegram:          ['ti-brand-telegram', 'Telegram'],
  // ⚠️ Faltaban los dos que sirve Chatwoot, y el respaldo `|| [icono, tipo]` no falla: pinta el
  // **código crudo**. En pantalla se leía «WEB_CHAT», que es el nombre de la columna, no el del
  // canal. Un canal nuevo sin fila acá se ve descuidado sin que nada avise, así que agregar un tipo
  // en `canales_tipos` obliga a pasar por este mapa.
  web_chat:          ['ti-message-2', 'Chat web'],
  email:             ['ti-mail', 'Correo']
};

const datosCanal = (tipo) => CANAL[tipo] || ['ti-message-2', tipo || 'Canal'];

/**
 * La instancia elegida, o la primera si la elegida ya no existe -- una linea puede
 * borrarse desde el panel de admin mientras el portal esta abierto, y quedarse
 * apuntando a un id fantasma dejaria la pantalla vacia sin explicacion.
 */
function resolverActiva(instancias) {
  if (instancias.length <= 1) return instancias[0] || null;
  const guardada = sessionStorage.getItem('dv_instancia');
  const existe = instancias.some(i => i.id === guardada);
  return instancias.find(i => i.id === (existe ? guardada : instancias[0].id));
}

window.elegirInstancia = function(id) {
  instanciaActiva = id;
  // sessionStorage y no cookie: es una preferencia de pantalla, no una credencial.
  // Dura lo que dura la pestana, que es lo que se espera de un selector.
  sessionStorage.setItem('dv_instancia', id);
  renderInstances(instanciasCache);
  // La actividad se relee filtrada: si no, el registro seguiria mostrando lo de la
  // instancia anterior debajo de la tarjeta de la nueva.
  fetchActividad();
  fetchVentana();
  // ⚠️ Y las métricas, que era lo que faltaba: el selector cambiaba el resaltado y los cinco
  // gráficos se quedaban con los números del canal anterior. Un filtro que no filtra es peor que
  // no tenerlo -- el cliente lee cifras de otro canal creyendo que son del que eligió.
  fetchMetricas({ mostrarCarga: true });
};

function renderSelector(instancias) {
  if (instancias.length <= 1) return '';
  const botones = instancias.map(inst => {
    const [icono, etiqueta] = datosCanal(inst.tipo_canal);
    const activa = inst.id === instanciaActiva;
    const punto = inst.estado === 'open' ? 'var(--g)' : 'var(--r)';
    return `
      <button class="btn-sec"
              onclick="elegirInstancia('${inst.id}')"
              aria-pressed="${activa}"
              style="display:flex; align-items:center; gap:8px; ${activa ? 'border-color:var(--g);' : 'opacity:.6;'}">
        <span style="width:7px; height:7px; border-radius:50%; background:${punto};"></span>
        <i class="ti ${icono}"></i>
        ${telefonoLegible(inst.numero) || etiqueta}
      </button>`;
  }).join('');

  return `
    <div class="card" style="grid-column:1/-1; display:flex; flex-wrap:wrap; gap:10px; align-items:center;">
      <span class="card-label" style="margin:0 8px 0 0;">Canal activo</span>
      ${botones}
    </div>`;
}

/**
 * El resumen de Inicio: una ficha por canal, con su estado y si el asistente está encendido.
 *
 * ⚠️ **Es lo único que hay que ver para saber si el negocio está funcionando**, y por eso vive en
 * la pantalla de entrada y no dentro de Canales. Un canal caído tres vistas más adentro es un canal
 * caído que nadie ve.
 */
function renderResumen(instancias) {
  const caja = document.getElementById('pv-resumen');
  if (!caja) return;
  const badge = document.getElementById('pv-badge-canales');
  if (badge) badge.textContent = instancias.length ? String(instancias.length) : '';

  if (!instancias.length) {
    caja.innerHTML = `<div class="pv-vacio">Todavía no tienes canales conectados.</div>`;
    return;
  }
  caja.innerHTML = instancias.map(inst => {
    const [icono, etiqueta] = datosCanal(inst.tipo_canal);
    const ok = inst.estado === 'open';
    const titulo = telefonoLegible(inst.numero) || etiqueta;
    return `
      <button type="button" class="pv-canal ${ok ? '' : 'pv-canal--caido'}"
              onclick="irAVista('canales')" title="Ver este canal">
        <i class="ti ${icono}"></i>
        <div class="pv-canal-txt">
          <div class="pv-canal-nombre">${titulo}</div>
          <div class="pv-canal-estado">${ok ? 'Conectado' : 'Sin conexión'}</div>
        </div>
        <span class="pv-canal-ia ${inst.ia_atiende ? 'on' : ''}">
          ${etiquetaIA(inst)}
        </span>
      </button>`;
  }).join('');
}

// `ia_atiende` es el estado efectivo (asistente encendido, línea encendida y servicio al día); el
// interruptor de la línea solo no alcanza: con el asistente apagado decía «IA activa».
function etiquetaIA(inst) {
  if (inst.ia_atiende) return 'IA activa';
  return inst.bot_activo ? 'Asistente apagado' : 'IA en pausa';
}

function renderInstances(instancias) {
  const container = document.getElementById('instances-container');
  if (!instancias || instancias.length === 0) {
    container.innerHTML = `<div class="card"><div class="status-indicator"><div class="status-dot disconnected"></div><div><div class="status-text">Sin canales</div><div class="status-detail">No tienes números asociados a tu cuenta de agencia.</div></div></div></div>`;
    instanciasCache = [];
    instanciaActiva = null;
    return;
  }

  instanciasCache = instancias;
  const elegida = resolverActiva(instancias);
  instanciaActiva = elegida ? elegida.id : null;

  const cajaSel = document.getElementById('pv-selector-metricas');
  if (cajaSel) cajaSel.innerHTML = renderSelector(instancias);
  renderResumen(instancias);

  // ⚠️ **Se dibujan TODAS** (7.9). Antes se dibujaba sólo la activa, con el argumento de que el
  // selector daba contexto -- razonable con dos líneas de WhatsApp, y falso desde que hay canales
  // distintos: con cuatro, el cliente no podía ver de un vistazo si estaban todos arriba, que es
  // justamente lo que viene a mirar. El selector no desaparece: se mudó a Métricas, que es lo único
  // que de verdad filtra.
  const visibles = instancias;

  let html = '';
  visibles.forEach(inst => {
    const isConnected = inst.estado === 'open';
    const dotClass = isConnected ? 'connected' : 'disconnected';
    const statusTxt = isConnected ? 'Conectado' : 'Desconectado';
    // Renderizado condicional por tipo de instancia (3.5). Una linea de Cloud API NO
    // tiene QR: el numero se vincula en Meta, no escaneando nada. Mostrarle un QR al
    // cliente seria pedirle que haga algo que no existe.
    // Va ANTES de statusDet: declararlo despues lo dejaba en zona muerta temporal y
    // cada render moria con ReferenceError, que fetchEstado mostraba como "Error de Red".
    const oficial = inst.proveedor === 'meta_cloud';
    // El QR y el texto de reconexion cuelgan de "¿vive en Evolution?", NO de "¿no es de
    // Meta?" (7.1, 2 ago 2026). Con el tercer proveedor --los canales que sirve Chatwoot:
    // Telegram, chat web, correo-- preguntar por la negativa hacia que un canal sano se
    // dibujara como un WhatsApp caido, ofreciendole al cliente escanear un QR inexistente.
    const enEvolution = inst.proveedor === 'evolution';
    // El icono y la etiqueta salen de tipo_canal, no cableados a WhatsApp: el modelo
    // es multicanal desde 1.12 y una linea de Instagram mostrandose con el icono verde
    // de WhatsApp es una mentira barata que despues cuesta cara.
    // Sube aca porque `statusDet` ya la usa para el caso Chatwoot -- ver el aviso de zona
    // muerta temporal de arriba, que es el mismo motivo por el que el orden importa.
    const [iconoCanal, etiquetaCanal] = datosCanal(inst.tipo_canal);
    // Sube hasta acá porque `statusDet` lo usa: con un código en vuelo el renglón de estado no
    // puede seguir diciendo «escaneá el QR», que es la otra pantalla de WhatsApp y el QR ya ni
    // se está dibujando. Declararlo más abajo lo dejaba en zona muerta temporal -- ver el aviso
    // de `oficial`, que es el mismo error.
    const codigo = String(inst.pairing_code ?? '').replace(/[^A-Z0-9]/gi, '');
    const statusDet = isConnected
      ? (enEvolution ? 'WhatsApp activo y funcionando'
        : oficial ? 'API oficial de WhatsApp, activa'
        : etiquetaCanal + ' conectado')
      : (enEvolution ? (codigo ? 'Escanea el QR o escribe el código' : 'Escanea el QR para reconectar')
        : oficial ? 'La linea oficial no responde. Escribinos y la revisamos'
        : 'Este canal no responde. Escribinos y lo revisamos');
    const qrHtml = (enEvolution && !isConnected && inst.qr) ? `<img class="qr-img" src="${inst.qr.startsWith('data:') ? inst.qr : 'data:image/png;base64,'+inst.qr}" alt="QR WhatsApp"/><div class="qr-instruction">Abre WhatsApp → Dispositivos vinculados → Vincular dispositivo → Escanea este código</div>` : '';
    // Vincular sin escanear (23 oct 2026). **Los dos caminos a la vez**, y no uno en lugar del
    // otro: `requestPairingCode` no detiene el ciclo del QR --`genPairQR` corre con su propio
    // temporizador y nada lo cancela (`baileys/lib/Socket/socket.js`)--, así que mientras el código
    // vive el QR sigue siendo válido. Se separan con su propio título porque son **dos pantallas
    // distintas de WhatsApp**: «Vincular dispositivo» pide la cámara y «Vincular con número de
    // teléfono» pide el código, y leer una instrucción mientras se sigue la otra es el error que
    // hay que evitar.
    //
    // ⚠️ **El código lo trae el sondeo, no esta función.** Rota junto con el QR, así que el que se
    // dibuja es el que WhatsApp acepta ahora; uno retenido en pantalla sería un código que se
    // teclea entero para que falle. Por lo mismo el campo del número NO vive acá: este contenedor
    // se repinta completo cada vez que el código cambia.
    const codigoHtml = codigo
      ? '<div class="vinc-o">o escribe este código</div>'
        + `<div class="vinc-codigo">${codigo.slice(0, 4)}-${codigo.slice(4, 8)}</div>`
        + '<div class="qr-instruction">Abre WhatsApp → Dispositivos vinculados → '
        + '<b>Vincular con número de teléfono</b> y escribe este código.</div>'
      : '';
    const vincHtml = (enEvolution && !isConnected && OPERA_CANAL())
      ? `<button class="btn-link-qr" onclick="abrirVincular('${inst.id}')">${
        codigo ? 'Pedir el código para otro número' : 'No puedo escanear el QR'}</button>`
      : '';
    const botChecked = inst.bot_activo ? 'checked' : '';
    const botTitle = inst.bot_activo ? 'Asistente IA Activado' : 'Asistente IA Pausado';
    const tituloInstancia = telefonoLegible(inst.numero) || etiquetaCanal;
    // Cambiar de número sólo tiene sentido con la línea arriba: si está caída, lo que sirve es
    // el QR que ya se está mostrando. Va como texto y en el pie, no como un cuarto ícono en la
    // fila de acciones: deja el negocio sin atender, y un botón destructivo que se aprieta sin
    // leer es peor que uno que cuesta un clic más.
    const cambiarNumero = (OPERA_CANAL() && enEvolution && isConnected) ? `
        <div class="instance-footer">
          <button class="btn-sec btn-sec-peligro" onclick="desvincularInstancia('${inst.id}', this)">
            Cambiar de número
          </button>
          <div class="toggle-desc">Desvincula este número para conectar otro con el código QR.</div>
        </div>` : '';

    html += `
      <div class="card">
        <div class="instance-header">
          <div>
            <div class="instance-name">${tituloInstancia} <i class="ti ${iconoCanal}" style="color:var(--g)" title="${etiquetaCanal}"></i></div>
            <div class="instance-id">${oficial
              ? 'API oficial · Nº ' + (inst.phone_number_id || '—')
              // El identificador de un canal de Chatwoot no le dice nada al cliente: el del webchat
              // es un token interno y enseñarlo como «Instancia: …» invita a copiarlo creyendo que
              // es lo que hay que pegar. Para el correo sí sirve, porque es su casilla.
              : inst.tipo_canal === 'web_chat' ? 'En tu sitio web'
              : inst.tipo_canal === 'email' ? inst.identificador
              : 'Instancia: ' + inst.identificador}</div>
          </div>
          <div class="instance-actions">
            <button class="btn-sec" title="Refrescar estado" onclick="refrescarEstado(this)">
              <i class="ti ti-refresh"></i>
            </button>
            ${(OPERA_CANAL() && enEvolution) ? `
            <button class="btn-sec" title="Reconectar la línea"
                    onclick="reconectarInstancia('${inst.id}', this)">
              <i class="ti ti-plug-connected"></i>
            </button>` : ''}
          </div>
        </div>
        
        <div class="status-indicator">
          <div class="status-dot ${dotClass}"></div>
          <div>
            <div class="status-text">${statusTxt}</div>
            <div class="status-detail">${statusDet}</div>
            ${enEvolution ? `<div class="status-detail" style="opacity:.7">${textoUltimoPing(inst.ultimo_ping)}</div>` : ''}
          </div>
        </div>

        <div class="qr-container ${!isConnected ? 'visible' : ''}">
          ${qrHtml}
          ${codigoHtml}
          ${vincHtml}
        </div>
        ${bloqueInstalacion(inst.id)}

        <div class="toggle-row">
          <div>
            <div class="toggle-title" id="bot-title-${inst.id}">${botTitle}</div>
            <div class="toggle-desc">Atención autónoma e inteligente para esta línea.</div>
          </div>
          <label class="toggle-switch">
            <input type="checkbox" onchange="toggleBot('${inst.id}', this)" ${botChecked}
                   ${OPERA_CANAL() ? '' : 'disabled title="Tu rol no permite cambiar esto"'}/>
            <span class="toggle-slider"></span>
          </label>
        </div>
        ${cambiarNumero}
      </div>
    `;
  });
  
  if(container.innerHTML !== html) {
    container.innerHTML = html;
  }
}

async function toggleBot(instanciaId, checkboxEl) {
  if (botLocks[instanciaId]) { checkboxEl.checked = !checkboxEl.checked; return; }
  botLocks[instanciaId] = true;
  const nuevoEstado = checkboxEl.checked;
  const titleEl = document.getElementById(`bot-title-${instanciaId}`);
  
  try {
    const data = await apiFetch(API.botToggle, {
      method: 'POST',
      headers: {'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token},
      body: JSON.stringify({ instancia_id: instanciaId, bot_activo: nuevoEstado })
    });
    
    if (data.success) {
      titleEl.textContent = nuevoEstado ? 'Asistente IA Activado' : 'Asistente IA Pausado';
      // ⚠️ **La caché también, no sólo el DOM.** `elegirInstancia` repinta desde `instanciasCache`,
      // así que sin esta línea cambiar de canal y volver mostraba el valor de antes del cambio --
      // y como el usuario lo ve "mal", lo vuelve a apretar y termina dejándolo al revés de lo que
      // quería. Se corregía solo al siguiente sondeo, diez segundos después: el tiempo justo para
      // que ya hubiera hecho el segundo clic.
      const enCache = instanciasCache.find(i => i.id === instanciaId);
      if (enCache) enCache.bot_activo = nuevoEstado;
      Toast.success(nuevoEstado ? '<i class="ti ti-bolt"></i> Bot operativo en canal' : '<i class="ti ti-bolt-off"></i> Bot pausado en canal');
      // La acción queda registrada: se relee para que el usuario la vea aparecer en la
      // lista, que es lo que hace creíble el registro.
      fetchActividad();
  fetchVentana();
    } else {
      checkboxEl.checked = !nuevoEstado;
      Toast.error('<i class="ti ti-alert-triangle"></i> Error al cambiar bot');
    }

  } catch (err) {
    checkboxEl.checked = !nuevoEstado;
    // 403 no es un error de red ni una sesión caída: es que el rol no alcanza. Merece
    // su propio mensaje, o el usuario cree que el sistema falló.
    if (err.status === 403) Toast.error('Tu rol no permite cambiar el asistente de IA.');
    // El resto de los errores ya los avisa apiFetch.
  }
  botLocks[instanciaId] = false;
}

/**
 * Las derivaciones a una persona (§0-decies).
 *
 * Cuando el bot deriva, le dice al cliente final que **alguien del equipo fue avisado**. Hasta el
 * 21 ago 2026 eso no era cierto: la fila quedaba en la base y no la leía nada. Este bloque, el push
 * y el correo son las tres formas de que sí lo sea.
 *
 * ⚠️ **Es sólo para mirar: no hay botón de "atendida" y es a propósito.** Quien resuelve una
 * conversación es el agente que la atiende dentro de Chatwoot, no el dueño de la empresa dentro de
 * su panel; pedirle al dueño que declare resueltos los chats de su equipo —o a un técnico que entre
 * al panel del dueño— es inventar un trámite para un hecho que ya ocurre en otro lado. La derivación
 * se cierra sola cuando la conversación se marca resuelta en Chatwoot.
 */
async function fetchDerivaciones() {
  const caja = document.getElementById('ui-derivaciones');
  if (!caja) return;
  let lista = [];
  try {
    const r = await apiFetch(API.derivaciones, { hideErrorToast: true });
    lista = r?.derivaciones ?? [];
  } catch { return; }   // Un fallo de red no borra lo que ya está en pantalla.
  renderDerivaciones(lista);
}

function renderDerivaciones(lista) {
  const caja = document.getElementById('ui-derivaciones');
  if (!lista.length) { caja.hidden = true; caja.innerHTML = ''; return; }

  const filas = lista.map((d) => {
    // El motivo lo escribió el modelo, así que va escapado: es texto de terceros entrando al DOM.
    const motivo = (d.motivo || 'Sin motivo').replace(/[<>&]/g, (c) =>
      ({ '<': '&lt;', '>': '&gt;', '&': '&amp;' }[c]));
    const cuando = new Date(d.creado_en).toLocaleString('es-CL',
      { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
    return `<div class="deriv-item">
        <div class="deriv-texto">
          <strong>${motivo}</strong>
          <span class="deriv-cuando">Derivada el ${cuando}</span>
        </div>
        <div class="deriv-acciones">
          <a class="btn-sec" href="${d.link}" target="_blank" rel="noopener noreferrer">
            <i class="ti ti-message-2"></i> Abrir chat</a>
        </div>
      </div>`;
  }).join('');

  const n = lista.length;
  caja.innerHTML = `<div class="deriv-caja">
      <div class="deriv-cab">
        <i class="ti ti-headset"></i>
        <div>
          <strong>${n === 1 ? 'Un cliente pide' : `${n} clientes piden`} hablar con una persona</strong>
          <span>El asistente dejó de responder ahí. Vuelve a hacerse cargo solo, en cuanto marques la conversación como resuelta en el Centro de Mensajes.</span>
        </div>
      </div>
      ${filas}
    </div>`;
  caja.hidden = false;

}

async function fetchNotificaciones() {
  try {
    const data = await apiFetch(API.notificaciones, {
      headers: { 'Authorization': 'Bearer ' + token },
      hideErrorToast: true 
    });
    if (data.success && data.notificaciones) {
      renderNotificaciones(data.notificaciones);
    }
  } catch(e) {}
}

function renderNotificaciones(list) {
  const container = document.getElementById('noti-list-container');
  const badge = document.getElementById('noti-badge-dot');
  
  const unreadCount = list.filter(n => !n.leida).length;
  if (unreadCount > 0) {
    badge.classList.add('active');
  } else {
    badge.classList.remove('active');
  }
  
  if (list.length === 0) {
    container.innerHTML = `<div class="noti-empty"><i class="ti ti-bell-off"></i>Sin alertas activas</div>`;
    return;
  }
  
  let html = '';
  list.forEach(n => {
    html += `
      <div class="noti-item ${!n.leida ? 'unread' : ''}">
        <div>${n.mensaje}</div>
        <div class="noti-time">${n.creado_en}</div>
      </div>
    `;
  });
  container.innerHTML = html;
}

document.getElementById('btn-clear-noti').addEventListener('click', async () => {
  // ⚠️ Antes esto era un `fetch` suelto: sin `await`, sin credenciales y sin mirar la respuesta, con
  // el aviso de éxito disparado igual. La ruta contestaba 404 --no existía-- y al siguiente sondeo
  // de 10 s la campana volvía a encenderse con lo que se acababa de marcar. Un éxito que no depende
  // del resultado no es un éxito, es una animación.
  //
  // Va por `apiFetch`, que manda la cookie de sesión: el `Authorization: Bearer` de esta línea era
  // del esquema anterior a 2.13 y hoy no autentica nada.
  try {
    await apiFetch(API.notificaciones + '/leer', { method: 'POST', credentials: 'include' });
    document.getElementById('noti-badge-dot').classList.remove('active');
    Toast.success('<i class="ti ti-check"></i> Notificaciones marcadas como leídas');
    // Se releen: la lista sigue en pantalla y sus filas tienen que dejar de verse como nuevas.
    await fetchNotificaciones();
  } catch (e) {
    Toast.error('No se pudieron marcar como leídas.');
  }
});

// === NUEVA FUNCIÓN DE LOGOUT EXPLÍCITO ===
async function performLogout() {
  clearInterval(refreshInterval);
  
  if (token) {
    try {
      // Sin cuerpo: el backend cierra la sesión por la cookie de refresco, que es la
      // que hay que matar -- el token de acceso caduca solo en 15 minutos.
      await apiFetch(API.logout, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      }, true); // Silencioso para evitar un toast rojo si el usuario se queda sin red justo al salir
    } catch (e) {
      console.warn('No se pudo invalidar token en backend, procediendo a limpieza local.');
    }
  }

  clearSession();
  showScreen('login-screen');
  document.getElementById('login-email').value = '';
  document.getElementById('login-password').value = '';
}

// Actualizamos el listener para que llame a nuestra nueva función
document.getElementById('btn-logout').addEventListener('click', performLogout);

// Una sesion de soporte llega sin datos de pantalla -- solo el token -- asi que primero
// hay que preguntar a quien se esta mirando. Una sesion normal ya los tiene guardados.
if(loadSession() === 'soporte'){
  apiFetch(API.sesion, { headers: { 'Authorization': 'Bearer ' + token } })
    .then(d => {
      if(!d || !d.success) throw new Error('sesion invalida');
      usuario = d.usuario; cliente = d.cliente; suscripcion = d.suscripcion;
      initDashboard();
      Toast.success('Sesión de soporte. Dura 30 minutos y quedó registrada.');
    })
    .catch(() => { clearSession(); showScreen('login-screen'); });
} else if(loadSession()){
  initDashboard();
} else {
  // ⚠️ **No haber guardado el perfil no significa no tener sesión.**
  //
  // La sesión de verdad son las dos cookies httpOnly de 2.13 y el refresco dura 7 días. Lo que hay
  // en `sessionStorage` son datos de pantalla — y `sessionStorage` **muere al cerrar la pestaña**.
  // En un escritorio eso no se nota porque la pestaña queda abierta; en el teléfono, cerrar la app
  // instalada termina el contexto, se borra el perfil y el portal mostraba la pantalla de acceso
  // con la cookie viva al lado. Se veía como «la sesión dura muy poco» y no era la sesión: era el
  // portal preguntándole a la memoria equivocada.
  //
  // Así que se le pregunta a quien sabe. Si la cookie sirve, `/portal/sesion` devuelve el perfil y
  // se entra directo; si no, recién ahí la pantalla de acceso. Se paga una petición en el arranque
  // frío, que es exactamente el momento en que se puede pagar.
  document.getElementById('login-screen').classList.add('comprobando');
  apiFetch(API.sesion, { hideErrorToast: true })
    .then(d => {
      if(!d || !d.success) throw new Error('sin sesion');
      token = 'cookie'; usuario = d.usuario; cliente = d.cliente; suscripcion = d.suscripcion;
      saveSession(token, usuario, cliente, suscripcion);
      initDashboard();
    })
    // Cualquier fallo termina en la pantalla de acceso: un 401 porque no hay sesión, y un fallo de
    // red porque sin API no hay portal que mostrar. La diferencia importa para el mensaje, no para
    // la pantalla — y `apiFetch` ya reintenta el 401 una vez por `/portal/refresh`, que es el caso
    // normal al volver a abrir la app después de más de 15 minutos.
    .catch(() => { showScreen('login-screen'); })
    .finally(() => document.getElementById('login-screen').classList.remove('comprobando'));
}


// === LÓGICA DE RECUPERACIÓN DE CONTRASEÑA ===

// 1. Solicitar el Código
document.getElementById('btn-request-code').addEventListener('click', async () => {
  const email = document.getElementById('recovery-email').value.trim();
  const btn = document.getElementById('btn-request-code');
  const err = document.getElementById('recovery-error');
  err.style.display = 'none';

  if (!email) { err.textContent = 'Ingresa un correo válido.'; err.style.display = 'block'; return; }

  btn.disabled = true; btn.innerHTML = '<span class="spinner"></span> Procesando...';

  try {
    const data = await apiFetch(API.recoveryRequest, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email })
    });

    if (data.success) {
      Toast.success('Si el correo existe, recibirás un código de 6 dígitos.');
      document.getElementById('recovery-step-1').style.display = 'none';
      document.getElementById('recovery-step-2').style.display = 'block';
      document.getElementById('recovery-subtitle').textContent = `Código enviado a ${email}`;
    } else {
      err.textContent = data.mensaje || 'Error al procesar la solicitud.';
      err.style.display = 'block';
    }
  } catch (e) {
    err.textContent = 'Error de conexión con el servidor.'; err.style.display = 'block';
  }
  btn.disabled = false; btn.textContent = 'Solicitar Código';
});

// 2. Cambiar la Contraseña
document.getElementById('btn-reset-password').addEventListener('click', async () => {
  const email = document.getElementById('recovery-email').value.trim();
  const codigo = document.getElementById('recovery-code').value.trim();
  const new_password = document.getElementById('recovery-new-password').value;
  const btn = document.getElementById('btn-reset-password');
  const err = document.getElementById('recovery-error');
  err.style.display = 'none';

  if (!email || codigo.length !== 6 || !new_password) {
    err.textContent = 'Ingresa tu correo, el código de 6 dígitos y tu nueva contraseña.';
    err.style.display = 'block'; return;
  }

  btn.disabled = true; btn.innerHTML = '<span class="spinner"></span> Validando...';

  try {
    const data = await apiFetch(API.recoveryReset, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, codigo, new_password })
    });

    if (data.success) {
      Toast.success('<i class="ti ti-check"></i> Contraseña actualizada correctamente.');

      // Limpiamos los campos
      document.getElementById('recovery-code').value = '';
      document.getElementById('recovery-new-password').value = '';

      // LA MAGIA ESTÁ AQUÍ: Llamamos a la función que cierra el popup y resetea la vista.
      // Como el modal está flotando sobre el login, al cerrarlo el usuario ya estará en el Home.
      closeModal();
    } else {
      err.textContent = data.mensaje || 'Código inválido o expirado.';
      err.style.display = 'block';
    }
  } catch (e) {
    err.textContent = 'Error de conexión con el servidor.'; err.style.display = 'block';
  }
  btn.disabled = false; btn.textContent = 'Cambiar Contraseña';
});

// ==============================================
// LÓGICA DE PERFIL DE USUARIO (FASE 2)
// ==============================================

// 1. Agrega estos endpoints al inicio de tu archivo app.js en el objeto API (junto a los que ya tienes)
// API.perfilActualizar = 'https://n8n.deviant.cl/webhook/portal/perfil-actualizar';
// API.perfilCodigoCorreo = 'https://n8n.deviant.cl/webhook/portal/perfil-codigo-correo';
// API.perfilValidarCorreo = 'https://n8n.deviant.cl/webhook/portal/perfil-validar-correo';
// ==============================================
// SISTEMA INTEGRAL DE PERFIL (V3 ENTERPRISE)
// ==============================================

let base64FotoTemp = null;

// FIX: función centralizada para que el badge del header (#user-avatar)
// y el del popup (#avatar-display) siempre muestren lo mismo. Antes
// #user-avatar solo mostraba la inicial y nunca la foto, sin importar
// lo que hubiera guardado.
function updateAvatarDisplays() {
  const tieneFoto = typeof usuario !== 'undefined' && usuario && usuario.foto_perfil;
  const inicial = (typeof usuario !== 'undefined' && usuario && usuario.nombre) ? usuario.nombre.charAt(0).toUpperCase() : 'D';

  const badge = document.getElementById('user-avatar');
  if (badge) {
    if (tieneFoto) {
      badge.style.backgroundImage = `url(${usuario.foto_perfil})`;
      badge.textContent = '';
    } else {
      // ⚠️ **Cadena vacia y NO `'none'`.** El degradado violeta de `.user-avatar` **es** un
      // `background-image`, asi que `'none'` no "quita la foto": apaga tambien el degradado y deja
      // el circulo transparente con la inicial blanca encima -- invisible en modo claro, que es lo
      // que se veia en la barra de las tres anchuras. La cadena vacia borra la declaracion en
      // linea y devuelve el control a la hoja.
      badge.style.backgroundImage = '';
      badge.textContent = inicial;
    }
  }

  const avatar = document.getElementById('avatar-display');
  if (avatar) {
    if (tieneFoto) {
      avatar.style.backgroundImage = `url(${usuario.foto_perfil})`;
      avatar.textContent = '';
    } else {
      avatar.style.backgroundImage = '';
      avatar.textContent = inicial;
    }
  }
}
window.updateAvatarDisplays = updateAvatarDisplays;

window.openProfile = function() {
  const profileScreen = document.getElementById('profile-screen');
  if (!profileScreen) return;

  // Mostramos y forzamos renderizado para animación fluida
  profileScreen.classList.add('active');

  // Mapeo de datos seguros
  if (typeof usuario !== 'undefined' && usuario) {
    if(document.getElementById('prof-nombre')) document.getElementById('prof-nombre').value = usuario.nombre || '';
    if(document.getElementById('prof-apellido')) document.getElementById('prof-apellido').value = usuario.apellido || '';
    perfilTelefono()?.poner(usuario.telefono_personal || '');
    if(document.getElementById('prof-email')) document.getElementById('prof-email').value = usuario.email_login || '';
  }

  updateAvatarDisplays();
};

window.closeProfile = function() {
  const profileScreen = document.getElementById('profile-screen');
  if (!profileScreen) return;

  profileScreen.classList.remove('active');

  // Limpiamos contraseñas por seguridad
  if(document.getElementById('prof-nueva-clave')) document.getElementById('prof-nueva-clave').value = '';
  if(document.getElementById('prof-clave-actual')) document.getElementById('prof-clave-actual').value = '';
  base64FotoTemp = null;
};

// Evento: Cerrar al hacer clic en el fondo oscuro
window.closeProfileOutside = function(event) {
  const profileScreen = document.getElementById('profile-screen');
  if (event.target === profileScreen) {
    window.closeProfile();
  }
};

// Evento: Subir y Comprimir Foto
window.handleImageUpload = function(event) {
  const file = event.target.files[0];
  if (!file) return;

  const reader = new FileReader();
  reader.onload = function(e) {
    const img = new Image();
    img.onload = function() {
      const canvas = document.createElement('canvas');
      const MAX_SIZE = 400;
      let width = img.width, height = img.height;
      if (width > height) { if (width > MAX_SIZE) { height *= MAX_SIZE / width; width = MAX_SIZE; } }
      else { if (height > MAX_SIZE) { width *= MAX_SIZE / height; height = MAX_SIZE; } }

      canvas.width = width; canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);

      base64FotoTemp = canvas.toDataURL('image/webp', 0.8);
      const avatar = document.getElementById('avatar-display');
      avatar.style.backgroundImage = `url(${base64FotoTemp})`;
      avatar.textContent = '';
    };
    img.src = e.target.result;
  };
  reader.readAsDataURL(file);
};

// Evento: Guardado en n8n
window.saveProfile = async function() {
  const actual = document.getElementById('prof-clave-actual').value;
  if(!actual) {
    Toast.error("Debes ingresar tu <b>Contraseña Actual</b> para guardar los cambios.");
    return;
  }

  // A este número le llegan los avisos internos por WhatsApp (0144). Guardar como NULL uno que se
  // escribió y no se entendió deja a alguien sin avisos sin que nada lo diga.
  const tel = perfilTelefono();
  if (tel && !tel.vacio() && !tel.valor()) {
    Toast.error(MSG_TELEFONO);
    tel.enfocar();
    return;
  }

  const payload = {
    contrasena_actual: actual,
    datos_nuevos: {
      nombre: document.getElementById('prof-nombre').value.trim(),
      apellido: document.getElementById('prof-apellido').value.trim(),
      telefono_personal: perfilTelefono()?.valor() ?? null,
      email_login: document.getElementById('prof-email').value.trim(),
      nueva_contrasena: document.getElementById('prof-nueva-clave').value,
    }
  };

  if (base64FotoTemp) payload.datos_nuevos.foto_perfil = base64FotoTemp;

  const btn = document.getElementById('btn-save-profile');
  btn.disabled = true; btn.innerHTML = '<span class="spinner"></span> Guardando...';

  try {
    const data = await apiFetch(API.perfilActualizar, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(payload)
    });

    if(data.success) {
      Toast.success("Perfil actualizado con éxito.");
      // Actualizar memoria RAM
      usuario.nombre = payload.datos_nuevos.nombre;
      usuario.apellido = payload.datos_nuevos.apellido;
      usuario.telefono_personal = payload.datos_nuevos.telefono_personal;
      usuario.email_login = payload.datos_nuevos.email_login;
      if(base64FotoTemp) usuario.foto_perfil = base64FotoTemp;

      // FIX: antes esto solo quedaba en memoria (variable `usuario`) y se
      // perdía al recargar la página, porque sessionStorage seguía con los
      // datos viejos del login. Ahora se persiste también ahí.
      saveSession(token, usuario, cliente, suscripcion);

      // Re-renderizamos el nombre arriba a la derecha si cambió
      if(document.getElementById('user-name')) document.getElementById('user-name').textContent = usuario.nombre;
      // FIX: el badge del header nunca se refrescaba con la foto nueva
      updateAvatarDisplays();

      window.closeProfile();
    } else {
      Toast.error(data.mensaje || "Clave actual incorrecta.");
    }
  } catch (e) {
    console.error("Error al guardar perfil", e);
  }

  btn.disabled = false; btn.innerHTML = 'Guardar Cambios';
};

// Cierre inteligente del modal de perfil al hacer clic en el fondo difuminado externo
document.getElementById('profile-screen').addEventListener('click', function(e) {
  // Si el elemento donde se hizo clic es exactamente el contenedor padre (el fondo) y no la tarjeta blanca interna
  if (e.target === this) {
    window.closeProfile();
  }
});

// ==============================================
// FUNCIONES DE VALIDACIÓN DE CORREO FALTANTES
// ==============================================
window.startEmailChange = function() {
  const emailModal = document.getElementById('email-modal');
  if(emailModal) {
    emailModal.classList.add('active');
    document.getElementById('email-step-1').style.display = 'block';
    document.getElementById('email-step-2').style.display = 'none';
    // FIX UX: limpiar restos de un intento anterior (código y error)
    const codeInput = document.getElementById('email-verify-code');
    if (codeInput) codeInput.value = '';
    const err = document.getElementById('email-modal-error');
    if (err) err.style.display = 'none';
  }
};

window.closeEmailModal = function() {
  const emailModal = document.getElementById('email-modal');
  if(emailModal) emailModal.classList.remove('active');
};

// FIX: cerrar el modal de correo al hacer clic en el fondo oscuro (fuera de la tarjeta)
window.closeEmailModalOutside = function(event) {
  if (event.target === document.getElementById('email-modal')) {
    window.closeEmailModal();
  }
};

window.requestEmailCode = async function() {
  const btn = document.getElementById('btn-req-email-code');
  if(!btn) return;
  btn.disabled = true; btn.innerHTML = '<span class="spinner"></span> Enviando...';
  try {
    await apiFetch(API.perfilCodigoCorreo, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      // FIX: el workflow de recuperación espera "email", no "email_actual"
      body: JSON.stringify({ email: usuario.email_login })
    });
    document.getElementById('email-step-1').style.display = 'none';
    document.getElementById('email-step-2').style.display = 'block';
    Toast.success("Código enviado a tu correo actual.");
  } catch(e) {
    // FIX: antes este catch estaba vacío y el error quedaba invisible para el usuario
    console.error("Error al solicitar código de correo", e);
    Toast.error("No se pudo enviar el código. Intenta de nuevo.");
  }
  btn.disabled = false; btn.innerHTML = 'Enviar código de validación';
};

window.verifyEmailCode = async function() {
  const code = document.getElementById('email-verify-code').value.trim();
  const btn = document.getElementById('btn-verify-email-code');
  const err = document.getElementById('email-modal-error');
  err.style.display = 'none';

  if (code.length !== 6) { err.textContent = "El código debe tener 6 dígitos"; err.style.display = 'block'; return; }

  btn.disabled = true; btn.innerHTML = '<span class="spinner"></span> Validando...';
  try {
    const data = await apiFetch(API.perfilValidarCorreo, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ email_actual: usuario.email_login, codigo: code })
    });

    if(data.success) {
      Toast.success('<i class="ti ti-lock-open"></i> Correo desbloqueado.');
      closeEmailModal();
      const inputEmail = document.getElementById('prof-email');
      inputEmail.disabled = false;
      inputEmail.style.background = '#080808';
      inputEmail.style.color = '#ffffff';
      inputEmail.focus();
    } else {
      err.textContent = data.mensaje || "Código inválido.";
      err.style.display = 'block';
    }
  } catch(e) {
    // FIX: mismo problema que en requestEmailCode, el error quedaba invisible
    console.error("Error al validar código de correo", e);
    err.textContent = "Error de conexión. Intenta de nuevo.";
    err.style.display = 'block';
  }
  btn.disabled = false; btn.innerHTML = 'Validar y Desbloquear';
};

// (Nota: las funciones de perfil ya quedaron expuestas en `window` en el punto
// donde se definieron arriba — no hace falta reasignarlas de nuevo aquí.)

/* =========================================
   MÓDULO: CHATDEVIANT (MODAL Y POLLING)
   ========================================= */

document.addEventListener('DOMContentLoaded', () => {
    conectarAgente();
    const btnChatwoot = document.getElementById('btn-abrir-mensajes');
    const badgeChat = document.getElementById('chat-badge');
    const cajon = document.getElementById('chat-drawer');
    const fondo = document.getElementById('chat-backdrop');
    const btnCerrar = document.getElementById('btn-cerrar-chat');
    const frameWrapper = document.getElementById('chatDeviant-frame-wrapper');
    let iframeInjected = false;

    // El cajón se abre y se cierra desde tres lugares (botón, fondo, Escape), así que
    // el estado se toca en una sola función: si no, cerrar por Escape dejaba el
    // aria-expanded del botón en true y el lector de pantalla anunciaba lo contrario
    // de lo que se ve.
    const abrirCajon = () => {
        if (!cajon) return;

        // El iframe se inyecta una sola vez y recién al abrir: montarlo con la página
        // cargaría Chatwoot entero para todo cliente que nunca toca el botón.
        if (!iframeInjected) {
            // La URL con SSO la deja el polling en el botón; si todavía no llegó, el
            // login normal de Chatwoot es un destino válido, no un error.
            const ssoUrl = btnChatwoot?.getAttribute('data-sso-url') || 'https://chat.deviant.cl/app/login';
            frameWrapper.innerHTML = `
                <iframe
                    src="${ssoUrl}"
                    title="Centro de Mensajes"
                    style="width:100%; height:100%; border:none; background:transparent; display:block;">
                </iframe>
            `;
            iframeInjected = true;
        }

        // El cajón tapa la pantalla entera en móvil, así que un panel flotante encima es el mismo
        // defecto que el del tema sobre la bandeja.
        document.dispatchEvent(new CustomEvent(PANEL_ABIERTO, { detail: { origen: cajon } }));
        cajon.classList.add('chat-drawer--abierto');
        cajon.setAttribute('aria-hidden', 'false');
        if (fondo) fondo.classList.add('chat-backdrop--visible');
        if (btnChatwoot) btnChatwoot.setAttribute('aria-expanded', 'true');
        // Solo en pantallas donde el cajón tapa la página entera: en escritorio el
        // tablero sigue a la vista y bloquearle el scroll sería gratuito.
        if (window.innerWidth <= 700) document.body.classList.add('sin-scroll');
    };

    const cerrarCajon = () => {
        if (!cajon) return;
        cajon.classList.remove('chat-drawer--abierto');
        cajon.setAttribute('aria-hidden', 'true');
        if (fondo) fondo.classList.remove('chat-backdrop--visible');
        if (btnChatwoot) btnChatwoot.setAttribute('aria-expanded', 'false');
        document.body.classList.remove('sin-scroll');
    };

    if (btnChatwoot && cajon) {
        btnChatwoot.addEventListener('click', (e) => {
            e.preventDefault();
            if (cajon.classList.contains('chat-drawer--abierto')) cerrarCajon();
            else abrirCajon();
        });
    }

    if (btnCerrar) btnCerrar.addEventListener('click', (e) => { e.preventDefault(); cerrarCajon(); });
    if (fondo) fondo.addEventListener('click', cerrarCajon);
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && cajon?.classList.contains('chat-drawer--abierto')) cerrarCajon();
    });

    // Polling Dinámico: Consultar mensajes y guardar URL
    setInterval(async () => {
        try {
            // Se autentica con el token de sesion, igual que los otros siete
            // endpoints del portal. Antes se mandaba el email por query string:
            // eso convertia el endpoint en "dame un enlace de acceso a Chatwoot
            // para cualquier correo que yo sepa".
            if (!token) return;

            const data = await apiFetch(API.chatStatus, {
                headers: { 'Authorization': 'Bearer ' + token },
                hideErrorToast: true
            });

            if (data) {
                // Actualizamos el número rojo del botón. En cero se esconde: pegado a
                // un icono de la barra, un globo rojo permanente que dice "0" se lee
                // como una alerta que no lo es.
                if (data.unread_count !== undefined && badgeChat) {
                    badgeChat.textContent = data.unread_count;
                    badgeChat.classList.toggle('chat-badge--vacio', !Number(data.unread_count));
                }
                
                // Guardamos la URL mágica directamente en el botón para cuando el usuario haga clic
                if (data.sso_url && btnChatwoot) {
                    btnChatwoot.setAttribute('data-sso-url', data.sso_url);
                }
            }
        } catch (error) {
            // Silencioso
        }
    }, 30000); 
});

// Reconocimiento de usuario al ocupar widget de chatwoot

// Esperar a que el widget de Chatwoot esté listo en la pantalla
window.addEventListener("chatwoot:ready", function () {
    // Extraer los datos del usuario logueado en tu portal
    const userData = JSON.parse(sessionStorage.getItem('dv_usr'));

    if (userData && userData.email_login) {
        // Enviar la identidad a Chatwoot de forma invisible
        window.$chatwoot.setUser(userData.email_login, {
            email: userData.email_login,
            name: userData.nombre_usuario || 'Usuario Deviant'
        });
    }
});

// ---------------------------------------------------------------------------
// Service worker (6.7) — el portal se puede instalar en el telefono
// ---------------------------------------------------------------------------
//
// Se registra al final y sin bloquear nada: si falla, el portal funciona igual. La ruta es
// absoluta a la raiz porque el alcance de un service worker no puede ser mas ancho que el
// directorio desde el que se sirve.
//
// Lo que ese archivo NO cachea --la API y el HTML-- esta explicado en publico/sw.js, y es
// lo que evita repetir el incidente de 1.8: navegadores sirviendo un bundle viejo que le
// habla a un backend que ya no existe. Un service worker hace eso mismo pero sobreviviendo
// a un Ctrl+F5, asi que las reglas de ahi no son un detalle de rendimiento.
// El cuerpo se mudó a `sw-registro.js` cuando el calendario pasó a ser instalable aparte: los dos
// registran el mismo `sw.js` y con dos copias una se arregla y la otra no.
registrarSW();

// ---------------------------------------------------------------------------
// Tu asistente (E.4)
// ---------------------------------------------------------------------------
//
// Hasta la migración 0100 el prompt de un cliente se cambiaba con un UPDATE a mano: E.1 dejó la
// configuración en la base y cableó n8n para leerla, y no había ninguna ruta que la escribiera.
//
// ⚠️ **Acá sólo se muestran los campos que el flujo honra hoy.** `saludo`, `horario_atencion`,
// `mensaje_fuera_horario` y `escalar_a_humano` existen en la tabla y el workflow no los lee, así que
// dibujarlos sería prometer algo que no ocurre. Se agregan cuando se cableen, no antes.
let agenteActual = null;

async function cargarAgente() {
    const caja = document.getElementById('ui-agente-caja');
    if (!caja) return;
    let r;
    try {
        r = await apiFetch(API.agente, { credentials: 'include' });
    } catch { return; }
    agenteActual = r?.agente ?? null;
    // Sin agente configurado la tarjeta no se dibuja: es un cliente al que todavía no se le armó uno,
    // y una tarjeta vacía invita a apretar algo que no existe.
    if (!agenteActual) { caja.hidden = true; return; }
    caja.hidden = false;
    document.getElementById('agente-nombre').textContent = agenteActual.nombre || 'Tu asistente';
    const partes = [agenteActual.tipo === 'ia' ? 'Responde con IA' : 'Responde por menú de opciones'];
    partes.push(agenteActual.activo ? 'encendido' : 'apagado');
    if (agenteActual.version_actual > 0) partes.push(plural(agenteActual.version_actual, 'cambio', 'cambios'));
    document.getElementById('agente-detalle').textContent = partes.join(' · ');
}

/**
 * El interruptor del catálogo.
 *
 * ⚠️ **Sin productos cargados se muestra deshabilitado, no escondido.** Una opción que no está no
 * enseña que existe: el dueño que todavía no cargó su catálogo tiene que poder ver que esto lo está
 * esperando. Y guarda **al cambiar** y no con el botón de abajo: ése pide contraseña porque reescribe
 * el prompt, y esto es un permiso de una línea.
 */
/** El contador del tope. El número lo manda la base (`tope_personalidad`): dos números que tienen
 *  que coincidir son dos que se pueden separar, y el que sobra siempre es el del navegador. */
function contarPersonalidad() {
    const ta = document.getElementById('agente-form-prompt');
    const out = document.getElementById('agente-prompt-cuenta');
    if (!ta || !out || !agenteActual) return;
    const tope = Number(agenteActual.tope_personalidad ?? 2000);
    const n = ta.value.length;
    out.textContent = `${n} / ${tope}`;
    out.className = n > tope ? 'cat-no' : (n > tope * 0.9 ? 'cat-chip cat-chip--avisa' : '');
}

/**
 * Lo que el asistente sabe hacer, en palabras.
 *
 * ⚠️ **Nunca el texto funcional literal**: ese texto nombra las herramientas (`buscar_productos`,
 * `derivar_a_humano`) y describe la mecánica de la base de conocimiento. Mostrarlo sería volver al
 * problema que el cambio de formato vino a resolver. Pero no decir nada es peor: el negocio creería
 * que su texto es todo lo que el bot lee, y no entendería por qué contesta cosas que él no escribió.
 */
function pintarQueSabe() {
    const caja = document.getElementById('agente-sabe');
    if (!caja || !agenteActual) return;
    if (agenteActual.tipo !== 'ia') { caja.hidden = true; return; }
    const sabe = [
        'Responder por WhatsApp con el formato del canal',
        'No inventar precios ni condiciones, y no revelar como esta construido',
        agenteActual.tiene_kb ? 'Consultar la base de conocimiento de tu negocio' : null,
        agenteActual.con_agenda ? 'Consultar tu calendario y reservar horas' : null,
        agenteActual.catalogo_bot_activo ? 'Consultar tu catalogo, decir precios y mandar fotos' : null,
        agenteActual.planes_bot_activo ? 'Ofrecer tus planes con su precio y lo que incluyen' : null,
        agenteActual.escalar_a_humano ? 'Derivar la conversacion a una persona' : null,
    ].filter(Boolean);
    caja.innerHTML = '<strong>Ademas de lo que escribas, tu asistente ya sabe:</strong><ul>'
        + sabe.map((s) => `<li>${s}</li>`).join('')
        + '</ul><span class="agente-ayuda">Eso lo pone el sistema y no se puede editar. Cambia solo '
        + 'cuando cambian tu plan o tus ajustes.</span>';
    caja.hidden = false;
}

/**
 * Los documentos de la base de conocimiento: qué tiene cargado su negocio, de sólo lectura.
 *
 * Existe porque la pantalla ya le decía «tu asistente consulta los documentos de tu base de
 * conocimiento» y no había forma de ver **cuáles**. Subirlos y borrarlos es de Deviant, así que acá
 * no hay botones: el único pedido posible es por escrito, y eso ya lo dice el texto de arriba.
 *
 * ⚠️ **Si el asistente no los está consultando, se dice.** Una lista de documentos sin ese aviso se
 * lee como que el bot los está usando, que es peor que no mostrar nada.
 */
async function pintarConocimiento() {
    const caja = document.getElementById('agente-kb-docs');
    if (!caja) return;
    caja.hidden = true;
    if (!agenteActual || agenteActual.tipo !== 'ia' || !agenteActual.kb_propia) return;
    let datos;
    try {
        datos = await apiFetch(API.agenteConocimiento, { hideErrorToast: true });
    } catch { return; }   // el texto de ayuda ya explica el estado; una lista vacía no suma
    const docs = datos?.documentos ?? [];
    if (!docs.length) return;
    caja.innerHTML = '<strong>Documentos de tu base de conocimiento:</strong><ul>'
        + docs.map((d) => `<li>${esc(d.titulo)}`
            + `<span class="agente-ayuda"> · ${d.estado === 'listo' ? 'en uso' : 'procesando'}`
            + ` · actualizado ${new Date(d.actualizado_en).toLocaleDateString('es-CL')}</span></li>`).join('')
        + '</ul>'
        + (datos.consultada
            ? '<span class="agente-ayuda">Los sube y los actualiza Deviant: escríbenos lo que quieras cambiar.</span>'
            : `<span class="agente-ayuda">Tu asistente <strong>no los está consultando</strong>${
                datos.motivo ? `: ${esc(datos.motivo)}` : ''}.</span>`);
    caja.hidden = false;
}

function pintarInterruptorCatalogo() {
    const fila = document.getElementById('agente-fila-catalogo');
    const sw = document.getElementById('agente-form-catalogo');
    const ayuda = document.getElementById('agente-ayuda-catalogo');
    if (!fila || !sw || !agenteActual) return;
    // Sólo para agentes de IA: un bot de menú no llama herramientas.
    if (agenteActual.tipo !== 'ia') { fila.hidden = true; ayuda.textContent = ''; return; }

    const variantes = Number(agenteActual.catalogo_variantes ?? 0);
    fila.hidden = false;
    sw.checked = Boolean(agenteActual.catalogo_bot_activo);
    sw.disabled = variantes === 0;
    ayuda.textContent = variantes === 0
        ? 'Primero carga productos en Catálogo.'
        : (sw.checked
            ? `Consulta tus ${plural(variantes, 'producto', 'productos')}, dice su precio y manda fotos. No inventa precios ni ofrece descuentos.`
            : `Tienes ${plural(variantes, 'producto cargado', 'productos cargados')} y tu asistente NO los usa: si preguntan, dirá que no vendes productos.`);
}

/**
 * El interruptor de los planes. No viene en `agenteActual` --la config del agente no sabe de
 * planes-- así que se pide aparte, y hasta que llega la fila queda escondida.
 */
async function pintarInterruptorPlanes() {
    const fila = document.getElementById('agente-fila-planes');
    const sw = document.getElementById('agente-form-planes');
    const ayuda = document.getElementById('agente-ayuda-planes');
    if (!fila || !sw || !agenteActual) return;
    if (agenteActual.tipo !== 'ia') { fila.hidden = true; ayuda.textContent = ''; return; }
    let r;
    try { r = await apiFetch(API.planes, { credentials: 'include', hideErrorToast: true }); }
    catch { fila.hidden = true; ayuda.textContent = ''; return; }
    const visibles = Number(r?.ajustes?.visibles ?? 0);
    agenteActual.planes_bot_activo = Boolean(r?.ajustes?.bot_activo);
    agenteActual.planes_visibles = visibles;
    fila.hidden = false;
    sw.checked = agenteActual.planes_bot_activo;
    // Apagarlo siempre se puede; encenderlo sin planes visibles no cambiaría nada.
    sw.disabled = visibles === 0 && !sw.checked;
    ayuda.textContent = visibles === 0
        ? 'Primero carga tus planes en Catálogo › Planes.'
        : (sw.checked
            ? `Ofrece tus ${plural(visibles, 'plan', 'planes')} con su precio y lo que incluyen. No inventa planes ni descuentos.`
            : `Tienes ${plural(visibles, 'plan', 'planes')} y tu asistente NO los menciona.`);
    pintarQueSabe();
}

/**
 * El modo de respuesta en una frase (0244). Los segundos sobran en el modo inmediato: se esconden
 * en vez de dejar un número que no hace nada.
 */
function explicarModo() {
    const modo = document.getElementById('agente-form-modo').value;
    const campo = document.getElementById('agente-form-espera');
    const seg = Number(campo.value) || 0;
    campo.hidden = modo === 'inmediato';
    const s = plural(seg, 'segundo', 'segundos');
    document.getElementById('agente-ayuda-modo').textContent = {
        agrupar: `Espera ${s} sin mensajes nuevos y contesta todo junto. Si el cliente no para de escribir, contesta a los ${plural(seg * 3, 'segundo', 'segundos')} igual.`,
        ventana: `Espera ${s} desde el primer mensaje y contesta todo lo que llegó en ese tiempo.`,
        inmediato: 'Contesta cada mensaje por separado: si escriben tres seguidos, responde tres veces.',
    }[modo];
}

function abrirAgente() {
    if (!agenteActual) return;
    document.getElementById('agente-form-nombre').value = agenteActual.nombre ?? '';
    document.getElementById('agente-form-prompt').value = agenteActual.personalidad ?? '';
    contarPersonalidad();
    pintarQueSabe();
    document.getElementById('agente-form-activo').checked = Boolean(agenteActual.activo);
    document.getElementById('agente-form-modo').value = agenteActual.respuesta_modo ?? 'agrupar';
    document.getElementById('agente-form-espera').value = agenteActual.respuesta_espera_seg ?? 6;
    explicarModo();
    pintarInterruptorCatalogo();
    pintarInterruptorPlanes();
    document.getElementById('agente-form-motivo').value = '';
    document.getElementById('agente-form-clave').value = '';
    document.getElementById('agente-estado-guardado').textContent = '';

    const hor = agenteActual.horario_atencion ?? {};
    const restringido = Boolean(hor?.dias && Object.keys(hor.dias).length);
    document.getElementById('agente-form-horario-on').checked = restringido;
    document.getElementById('agente-form-fuera').value = agenteActual.mensaje_fuera_horario ?? '';
    document.getElementById('agente-horario-zona').textContent =
        `Se usa la hora de ${agenteActual.zona_horario ?? 'America/Santiago'}.`;
    pintarHorario(hor);
    refrescarHorario();

    // La base de conocimiento es una capacidad del plan, y "tu plan no la incluye" y "tu plan la
    // incluye y no has subido nada" son dos frases distintas: con un solo mensaje se confunden.
    const ayuda = document.getElementById('agente-ayuda-kb');
    if (agenteActual.tipo !== 'ia') {
        ayuda.textContent = 'Tu plan responde con un menú de opciones fijas: no hay instrucciones que escribir.';
    } else if (!agenteActual.kb_propia) {
        ayuda.textContent = 'Tu plan no incluye base de conocimiento: responde sólo con estas instrucciones.';
    } else if (!agenteActual.tiene_kb) {
        ayuda.textContent = 'Tu plan incluye base de conocimiento y no tiene documentos. Pídenoslos y los subimos.';
    } else {
        ayuda.textContent = 'Además de estas instrucciones, consulta los documentos de tu base de conocimiento.';
    }

    // Los documentos, si su plan los tiene. Se piden al abrir la pantalla y no al cargar el portal:
    // es una consulta que la mayoría de las sesiones no necesita.
    pintarConocimiento();

    // Un agente de menú no tiene prompt que editar; esconder es mejor que deshabilitar.
    const esIA = agenteActual.tipo === 'ia';
    document.getElementById('agente-form-prompt').closest('.profile-card')
        .querySelectorAll('[data-solo-ia]').forEach((n) => { n.hidden = !esIA; });

    document.getElementById('agente-screen').classList.add('active');
    cargarVersiones();
}

async function cargarVersiones() {
    const caja = document.getElementById('agente-versiones');
    caja.textContent = 'Cargando…';
    let r;
    try {
        r = await apiFetch(API.agenteVersiones, { credentials: 'include' });
    } catch { caja.textContent = 'No se pudo cargar el historial.'; return; }
    const versiones = r?.versiones ?? [];
    caja.innerHTML = '';
    if (!versiones.length) {
        caja.textContent = 'Todavía no has hecho cambios.';
        return;
    }
    for (const v of versiones) {
        const fila = document.createElement('div');
        fila.className = 'agente-version';
        const txt = document.createElement('div');
        const cuando = new Date(v.creado_en).toLocaleString('es-CL');
        txt.innerHTML = '';
        const t1 = document.createElement('strong');
        t1.textContent = `Versión ${v.version}`;
        const t2 = document.createElement('span');
        t2.className = 'agente-ayuda';
        t2.textContent = ` · ${cuando} · ${v.quien}${v.motivo ? ` · ${v.motivo}` : ''}`;
        txt.appendChild(t1); txt.appendChild(t2);
        fila.appendChild(txt);
        // La más nueva es la que está puesta: ofrecer "volver" a lo que ya rige es un botón que no
        // hace nada, y el usuario que lo aprieta cree que algo falló.
        if (v.version !== agenteActual.version_actual) {
            const b = document.createElement('button');
            b.className = 'btn-sec btn-sm';
            b.type = 'button';
            b.textContent = 'Volver a esta';
            b.addEventListener('click', () => revertirAgente(v.version));
            fila.appendChild(b);
        }
        caja.appendChild(fila);
    }
}

async function guardarAgente() {
    const btn = document.getElementById('btn-agente-guardar');
    const estado = document.getElementById('agente-estado-guardado');
    try {
        const clave = document.getElementById('agente-form-clave').value;
        if (!clave) {
            estado.textContent = '';
            Toast.error('Escribe tu contrasena para confirmar el cambio.');
            return;
        }
        // Recién acá se bloquea el botón: si falta la contraseña no hubo viaje que esperar.
        btn.disabled = true;
        estado.textContent = 'Guardando…';
        const cuerpo = {
            nombre: document.getElementById('agente-form-nombre').value.trim(),
            activo: document.getElementById('agente-form-activo').checked,
            motivo: document.getElementById('agente-form-motivo').value.trim() || null,
            horario_atencion: leerHorario(),
            mensaje_fuera_horario: document.getElementById('agente-form-fuera').value.trim(),
            clave,
        };
        if (agenteActual.tipo === 'ia') {
            cuerpo.respuesta_modo = document.getElementById('agente-form-modo').value;
            // En modo inmediato el campo está escondido: si viajara vacío, el error hablaría de un
            // campo que no se ve.
            if (cuerpo.respuesta_modo !== 'inmediato') {
                cuerpo.respuesta_espera_seg = document.getElementById('agente-form-espera').value;
            }
        }
        // El prompt sólo viaja si el agente es de IA: mandarlo vacío en un agente de menú lo dejaría
        // escrito en el historial como si alguien lo hubiera borrado.
        if (agenteActual.tipo === 'ia') {
            cuerpo.personalidad = document.getElementById('agente-form-prompt').value;
        }
        const r = await apiFetch(API.agente, {
            method: 'POST', credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(cuerpo),
            hideErrorToast: true,
        });
        document.getElementById('agente-form-clave').value = '';
        estado.textContent = `Guardado como versión ${r.version}.`;
        await cargarAgente();
        await cargarVersiones();
    } catch (e) {
        estado.textContent = '';
        Toast.error(e?.mensaje || 'No se pudo guardar.');
    } finally { btn.disabled = false; }
}

async function revertirAgente(version) {
    const clave = document.getElementById('agente-form-clave').value;
    if (!clave) { Toast.error('Escribe tu contrasena para volver a una version anterior.'); return; }
    if (!window.confirm(`¿Volver a la versión ${version}? Lo de ahora queda en el historial.`)) return;
    try {
        await apiFetch(API.agenteRevertir, {
            method: 'POST', credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ version, clave }),
            hideErrorToast: true,
        });
        Toast.success(`Volviste a la versión ${version}.`);
        await cargarAgente();
        abrirAgente();
    } catch (e) { Toast.error(e?.mensaje || 'No se pudo revertir.'); }
}

/** Prueba el prompt que está en pantalla contra un mensaje de ejemplo. No guarda nada. */
async function probarAgente() {
    const btn = document.getElementById('btn-agente-probar');
    const salida = document.getElementById('agente-prueba-salida');
    const mensaje = document.getElementById('agente-form-prueba').value.trim();
    if (!mensaje) { Toast.show('Escribe un mensaje de prueba.', 'error'); return; }
    btn.disabled = true;
    salida.hidden = false;
    salida.textContent = 'Probando…';
    try {
        const r = await apiFetch(API.agentePrevisualizar, {
            method: 'POST', credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                // Lo de la pantalla, no lo guardado: la pregunta es "¿sirve lo que acabo de escribir?".
                prompt_sistema: document.getElementById('agente-form-prompt').value,
                mensaje,
            }),
            hideErrorToast: true,
        });
        salida.textContent = r.respuesta || '(el modelo no devolvió texto)';
    } catch (e) {
        salida.hidden = true;
        Toast.error(e?.mensaje || 'No se pudo probar.');
    } finally { btn.disabled = false; }
}

let refrescarHorario = () => {};

function conectarAgente() {
    refrescarHorario = conectarHorario();
    document.getElementById('btn-agente-config')?.addEventListener('click', abrirAgente);
    document.getElementById('agente-form-prompt')?.addEventListener('input', contarPersonalidad);
    document.getElementById('agente-form-modo')?.addEventListener('change', explicarModo);
    document.getElementById('agente-form-espera')?.addEventListener('input', explicarModo);
    document.getElementById('agente-form-catalogo')?.addEventListener('change', async (ev) => {
        const sw = ev.target;
        sw.disabled = true;
        try {
            await apiFetch(API.catalogoAjustes, {
                method: 'POST', credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ bot_activo: sw.checked }),
                hideErrorToast: true,
            });
        } catch (e) {
            // Se vuelve atrás: un interruptor que quedó marcado sin haberse guardado miente sobre lo
            // que el bot puede hacer, que es justo lo que no puede pasar con un permiso.
            sw.checked = !sw.checked;
            Toast.error(e?.mensaje || 'No se pudo guardar.');
            sw.disabled = false;
            return;
        }
        if (agenteActual) agenteActual.catalogo_bot_activo = sw.checked;
        pintarInterruptorCatalogo();
        // El resumen de arriba cambia con esto: si no se repinta, dice algo que ya no es cierto.
        pintarQueSabe();
        Toast.success(sw.checked ? 'Tu asistente ya puede usar el catálogo.' : 'Tu asistente dejó de usar el catálogo.');
    });
    document.getElementById('agente-form-planes')?.addEventListener('change', async (ev) => {
        const sw = ev.target;
        sw.disabled = true;
        try {
            await apiFetch(API.planesAjustes, {
                method: 'POST', credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ bot_activo: sw.checked }),
                hideErrorToast: true,
            });
        } catch (e) {
            sw.checked = !sw.checked;
            Toast.error(e?.mensaje || 'No se pudo guardar.');
            sw.disabled = false;
            return;
        }
        Toast.success(sw.checked ? 'Tu asistente ya ofrece tus planes.' : 'Tu asistente dejó de ofrecer tus planes.');
        await pintarInterruptorPlanes();
    });
    document.getElementById('btn-agente-probar')?.addEventListener('click', probarAgente);
    document.getElementById('agente-form-prueba')?.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') { e.preventDefault(); probarAgente(); }
    });
    document.getElementById('btn-agente-cerrar')?.addEventListener('click',
        () => document.getElementById('agente-screen').classList.remove('active'));
    document.getElementById('btn-agente-guardar')?.addEventListener('click', guardarAgente);
    document.getElementById('agente-screen')?.addEventListener('click', (e) => {
        if (e.target.id === 'agente-screen') e.currentTarget.classList.remove('active');
    });
}

// ---------------------------------------------------------------------------
// Horario de atención del asistente (0102)
// ---------------------------------------------------------------------------
//
// El jsonb usa el día de la semana de `EXTRACT(dow)` de Postgres —0 = domingo—, la misma convención
// que `disponibilidad` en el calendario. Acá se muestran empezando el lunes, que es como trabaja
// cualquier negocio, pero **el número que se guarda no cambia**: renombrar el dato para que calce
// con la pantalla es justo lo que obliga a traducir en los dos sentidos para siempre.
const DIAS_BOT = [[1, 'Lunes'], [2, 'Martes'], [3, 'Miércoles'], [4, 'Jueves'],
                  [5, 'Viernes'], [6, 'Sábado'], [0, 'Domingo']];

/** Dibuja los siete días con sus tramos, desde el jsonb guardado. */
function pintarHorario(horario) {
    const caja = document.getElementById('agente-horario-dias');
    caja.innerHTML = '';
    const dias = horario?.dias ?? {};
    for (const [dow, nombre] of DIAS_BOT) {
        const tramos = dias[String(dow)] ?? [];
        const fila = document.createElement('div');
        fila.className = 'agente-dia';
        fila.dataset.dow = dow;

        const izq = document.createElement('label');
        izq.className = 'agente-dia-nombre';
        const chk = document.createElement('input');
        chk.type = 'checkbox';
        chk.className = 'agente-dia-on';
        chk.checked = tramos.length > 0;
        izq.appendChild(chk);
        izq.appendChild(document.createTextNode(nombre));
        fila.appendChild(izq);

        const rangos = document.createElement('div');
        rangos.className = 'agente-rangos';
        const agregar = (desde = '09:00', hasta = '18:00') => {
            const r = document.createElement('div');
            r.className = 'agente-rango';
            const a = document.createElement('input');
            a.type = 'time'; a.className = 'input agente-hora'; a.value = desde;
            const b = document.createElement('input');
            b.type = 'time'; b.className = 'input agente-hora'; b.value = hasta;
            const x = document.createElement('button');
            x.type = 'button'; x.className = 'agente-quitar'; x.textContent = '×';
            x.title = 'Quitar este tramo';
            x.addEventListener('click', () => r.remove());
            r.append(a, document.createTextNode('a'), b, x);
            rangos.appendChild(r);
        };
        for (const t of tramos) agregar((t[0] || '').slice(0, 5), (t[1] || '').slice(0, 5));
        if (!tramos.length) agregar();

        const mas = document.createElement('button');
        mas.type = 'button'; mas.className = 'agente-mas'; mas.textContent = '+ tramo';
        // Un segundo tramo es la hora de colación, que es el caso real que parte un día en dos.
        mas.title = 'Para partir el día, por ejemplo por colación';
        mas.addEventListener('click', () => agregar('15:00', '18:00'));

        const der = document.createElement('div');
        der.className = 'agente-dia-ctl';
        der.append(rangos, mas);
        fila.appendChild(der);

        const refrescar = () => { der.style.opacity = chk.checked ? '1' : '.35'; der.style.pointerEvents = chk.checked ? '' : 'none'; };
        chk.addEventListener('change', refrescar);
        refrescar();
        caja.appendChild(fila);
    }
}

/** Lee la pantalla y arma el jsonb. Un día destildado no aporta clave: eso es "no se atiende". */
function leerHorario() {
    if (!document.getElementById('agente-form-horario-on').checked) return {};
    const dias = {};
    for (const fila of document.querySelectorAll('#agente-horario-dias .agente-dia')) {
        if (!fila.querySelector('.agente-dia-on').checked) continue;
        const tramos = [];
        for (const r of fila.querySelectorAll('.agente-rango')) {
            const [a, b] = [...r.querySelectorAll('.agente-hora')].map((i) => i.value);
            // Un tramo incompleto o al revés se descarta en vez de guardarse: guardado, el bot
            // quedaría mudo ese día y nada en la pantalla lo explicaría.
            if (a && b && a < b) tramos.push([a, b]);
        }
        if (tramos.length) dias[fila.dataset.dow] = tramos;
    }
    return Object.keys(dias).length ? { dias } : {};
}

function conectarHorario() {
    const on = document.getElementById('agente-form-horario-on');
    const refrescar = () => {
        document.getElementById('agente-horario-dias').hidden = !on.checked;
        document.getElementById('agente-horario-msg').hidden = !on.checked;
    };
    on?.addEventListener('change', refrescar);
    return refrescar;
}
